<template>
  <div class="min-h-screen bg-[#f7f4ef] dark:bg-[#121212]">
    <!-- Sticky Top Bar -->
    <div class="sticky top-0 z-50 bg-white dark:bg-[#121212] border-b border-slate-200 dark:border-slate-700 shadow-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <div class="flex items-center space-x-4">
            <h1 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {{ t('checkout.title') }}
            </h1>
            <div class="text-sm text-slate-500 dark:text-slate-400">
              {{ t('checkout.items', { count: cartItems.length }) }}
            </div>
          </div>
          <div class="flex items-center space-x-4">
            <div class="text-right">
              <div class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {{ formatPrice(total) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button
        class="mb-4 text-blue-600 dark:text-blue-400 hover:underline"
        @click="goBackToCart"
      >
        {{ t('checkout.back') }}
      </button>
      <div class="grid grid-cols-1 2xl:grid-cols-[minmax(0,2fr)_minmax(420px,460px)] gap-8">
        <!-- Main Checkout Form -->
        <div class="space-y-8">
          <!-- Delivery Address -->
          <div
            data-testid="delivery-address"
            class="bg-white dark:bg-[#1b1b1b] rounded-2xl p-8 border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <h2 class="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
              {{ t('checkout.deliveryAddress') }}
            </h2>
            
            <form
              class="space-y-6"
              @submit.prevent="submitOrder"
            >
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    for="firstName"
                    class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                  >
                    {{ t('checkout.labels.firstName') }}
                    <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                  </label>
                  <input
                    id="firstName"
                    v-model="form.firstName"
                    data-testid="first-name"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    :placeholder="t('checkout.placeholders.firstName')"
                    @blur="scheduleSync"
                  >
                </div>
                <div>
                  <label
                    for="lastName"
                    class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                  >
                    {{ t('checkout.labels.lastName') }}
                    <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                  </label>
                  <input
                    id="lastName"
                    v-model="form.lastName"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    :placeholder="t('checkout.placeholders.lastName')"
                    @blur="scheduleSync"
                  >
                </div>
              </div>

              <div>
                <label
                  for="company"
                  class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                >
                  {{ t('checkout.labels.company') }}
                </label>
                <input
                  id="company"
                  v-model="form.company"
                  type="text"
                  class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  :placeholder="t('checkout.placeholders.company')"
                  @blur="scheduleSync"
                >
              </div>

              <div class="relative">
                <label
                  for="address"
                  class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                >
                  {{ t('checkout.labels.address') }}
                  <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                </label>
                <input
                  id="address"
                  v-model="form.address"
                  type="text"
                  required
                  class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  :placeholder="t('checkout.placeholders.address')"
                  autocomplete="street-address"
                  @input="handleAddressInput"
                  @focus="addressFocused = true"
                  @blur="handleAddressBlur"
                >
                <div
                  v-if="addressStatus === 'loading'"
                  class="absolute left-0 right-0 mt-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-lg"
                >
                  {{ addressLoadingText }}
                </div>
                <ul
                  v-if="addressSuggestions.length && addressFocused"
                  class="absolute left-0 right-0 mt-2 max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg z-20"
                >
                  <li
                    v-for="(suggestion, index) in addressSuggestions"
                    :key="`${suggestion.label}-${index}`"
                    class="px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-950/40 cursor-pointer"
                    @mousedown.prevent="applyAddressSuggestion(suggestion)"
                  >
                    {{ suggestion.label }}
                  </li>
                </ul>
                <p
                  v-if="addressHint"
                  class="mt-2 text-sm text-slate-500"
                >
                  {{ addressHint }}
                </p>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label
                    for="postalCode"
                    class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                  >
                    {{ t('checkout.labels.postalCode') }}
                    <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                  </label>
                  <input
                    id="postalCode"
                    v-model="form.postalCode"
                    data-testid="postal-code"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    :placeholder="t('checkout.placeholders.postalCode')"
                    @blur="scheduleSync"
                  >
                </div>
                <div>
                  <label
                    for="city"
                    class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                  >
                    {{ t('checkout.labels.city') }}
                    <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                  </label>
                  <input
                    id="city"
                    v-model="form.city"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    :placeholder="t('checkout.placeholders.city')"
                    @blur="scheduleSync"
                  >
                </div>
                <div>
                  <label
                    for="country"
                    class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                  >
                    {{ t('checkout.labels.country') }}
                    <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                  </label>
                  <select
                    id="country"
                    v-model="form.country"
                    required
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    @change="scheduleSync"
                  >
                    <option
                      v-for="country in countryOptions"
                      :key="country.code"
                      :value="country.code"
                    >
                      {{ country.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                <div class="md:col-span-2">
                  <label
                    for="phone"
                    class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                  >
                    {{ t('checkout.labels.phone') }}
                    <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                  </label>
                  <input
                    id="phone"
                    v-model="form.phone"
                    data-testid="phone"
                    type="tel"
                    required
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    :placeholder="t('checkout.placeholders.phone')"
                    @blur="scheduleSync"
                  >
                </div>
                <p class="text-sm text-slate-500 md:pt-9">
                  {{ t('checkout.phoneHint') }}
                </p>
              </div>

              <div>
                <label
                  for="email"
                  class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                >
                  {{ t('checkout.labels.email') }}
                  <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                </label>
                <input
                  id="email"
                  v-model="form.email"
                  data-testid="email"
                  type="email"
                  required
                  class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  :placeholder="t('checkout.placeholders.email')"
                  @blur="scheduleSync"
                >
              </div>

              <div class="flex items-center">
                <input
                  id="billing-same"
                  v-model="billingSameAsShipping"
                  type="checkbox"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                >
                <label
                  for="billing-same"
                  class="ml-2 block text-sm text-slate-700 dark:text-slate-200"
                >
                  {{ t('checkout.billing.sameAsShipping') }}
                </label>
              </div>

              <div
                v-if="!billingSameAsShipping"
                class="border-t border-slate-200 pt-6 space-y-6"
              >
                <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
                  {{ t('checkout.billing.title') }}
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      for="billingFirstName"
                      class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                    >
                      {{ t('checkout.labels.firstName') }}
                      <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                    </label>
                    <input
                      id="billingFirstName"
                      v-model="billing.firstName"
                      type="text"
                      required
                      class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      :placeholder="t('checkout.placeholders.firstName')"
                      @blur="scheduleSync"
                    >
                  </div>
                  <div>
                    <label
                      for="billingLastName"
                      class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                    >
                      {{ t('checkout.labels.lastName') }}
                      <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                    </label>
                    <input
                      id="billingLastName"
                      v-model="billing.lastName"
                      type="text"
                      required
                      class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      :placeholder="t('checkout.placeholders.lastName')"
                      @blur="scheduleSync"
                    >
                  </div>
                </div>
                <div>
                  <label
                    for="billingCompany"
                    class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                  >
                    {{ t('checkout.labels.company') }}
                  </label>
                  <input
                    id="billingCompany"
                    v-model="billing.company"
                    type="text"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    :placeholder="t('checkout.placeholders.company')"
                    @blur="scheduleSync"
                  >
                </div>
                <div>
                  <label
                    for="billingAddress"
                    class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                  >
                    {{ t('checkout.labels.address') }}
                    <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                  </label>
                  <input
                    id="billingAddress"
                    v-model="billing.address"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    :placeholder="t('checkout.placeholders.address')"
                    @blur="scheduleSync"
                  >
                </div>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label
                      for="billingPostalCode"
                      class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                    >
                      {{ t('checkout.labels.postalCode') }}
                      <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                    </label>
                    <input
                      id="billingPostalCode"
                      v-model="billing.postalCode"
                      type="text"
                      required
                      class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      :placeholder="t('checkout.placeholders.postalCode')"
                      @blur="scheduleSync"
                    >
                  </div>
                  <div>
                    <label
                      for="billingCity"
                      class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                    >
                      {{ t('checkout.labels.city') }}
                      <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                    </label>
                    <input
                      id="billingCity"
                      v-model="billing.city"
                      type="text"
                      required
                      class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      :placeholder="t('checkout.placeholders.city')"
                      @blur="scheduleSync"
                    >
                  </div>
                  <div>
                    <label
                      for="billingCountry"
                      class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                    >
                      {{ t('checkout.labels.country') }}
                      <span class="ml-1 text-red-600 dark:text-red-400">*</span>
                    </label>
                    <select
                      id="billingCountry"
                      v-model="billing.country"
                      required
                      class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      @change="scheduleSync"
                    >
                      <option
                        v-for="country in countryOptions"
                        :key="country.code"
                        :value="country.code"
                      >
                        {{ country.label }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- B2B Fields -->
              <div
                v-if="isB2BCustomer"
                class="border-t border-slate-200 pt-6"
              >
                <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
                  {{ t('checkout.b2b.title') }}
                </h3>
                <div>
                  <label
                    for="vatId"
                    class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
                  >
                    {{ t('checkout.labels.vatId') }}
                  </label>
                  <input
                    id="vatId"
                    v-model="form.vatId"
                    data-testid="vat-id"
                    type="text"
                    class="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ATU12345678"
                    @input="handleVatInput"
                    @blur="handleVatBlur"
                  >
                  <p
                    v-if="vatMessage"
                    class="mt-2 text-sm"
                    :class="vatMessageClass"
                  >
                    {{ vatMessage }}
                  </p>
                </div>
              </div>

              <div class="flex items-center">
                <input
                  id="b2b"
                  v-model="isB2BCustomer"
                  data-testid="b2b-customer"
                  type="checkbox"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                >
                <label
                  for="b2b"
                  class="ml-2 block text-sm text-slate-700 dark:text-slate-200"
                >
                  {{ t('checkout.labels.b2b') }}
                </label>
              </div>
            </form>
          </div>

          <!-- Shipping & Payment -->
          <div
            data-testid="shipping-payment"
            class="bg-white dark:bg-[#1b1b1b] rounded-2xl p-8 border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <h2 class="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
              {{ t('checkout.shippingPayment') }}
            </h2>
            
            <!-- Shipping Options -->
            <div class="space-y-4 mb-8">
              <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
                {{ t('checkout.shippingMethod') }}
              </h3>
              <div class="space-y-3">
                <div
                  v-for="option in shippingOptions"
                  :key="option.id"
                  class="flex items-center p-4 border border-slate-200 dark:border-slate-700 rounded-xl cursor-pointer hover:border-blue-300 dark:hover:border-blue-400 transition-colors"
                  :class="{ 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/40 selected': selectedShipping === option.id }"
                  @click="selectShipping(option.id)"
                >
                  <input
                    :id="`shipping-${option.id}`"
                    v-model="selectedShipping"
                    :data-testid="`shipping-${option.id}`"
                    :value="option.id"
                    type="radio"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300"
                  >
                  <div class="ml-3 flex-1">
                    <label
                      :for="`shipping-${option.id}`"
                      class="block text-sm font-medium text-slate-900 dark:text-slate-100 cursor-pointer"
                    >
                      {{ option.name }}
                    </label>
                    <p
                      v-if="option.description"
                      class="text-sm text-slate-500 dark:text-slate-400"
                    >
                      {{ option.description }}
                    </p>
                  </div>
                  <div class="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {{ option.price === 0 ? t('checkout.free') : formatPrice(option.price) }}
                  </div>
                </div>
                <div
                  v-if="shippingOptions.length === 0 && !shippingOptionsLoading"
                  class="text-sm text-slate-500 dark:text-slate-400"
                >
                  <span v-if="!isAddressReady">{{ t('checkout.shipping.noAddress') }}</span>
                  <span v-else>{{ t('checkout.shipping.unavailable') }}</span>
                </div>
                <div
                  v-if="shippingOptionsLoading"
                  class="text-sm text-slate-500 dark:text-slate-400"
                >
                  {{ t('checkout.shipping.loading') }}
                </div>
              </div>
            </div>

            <!-- Payment Options -->
            <div class="space-y-4">
              <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
                {{ t('checkout.paymentMethod') }}
              </h3>
              <div class="space-y-3">
                <div
                  v-for="option in paymentOptions"
                  :key="option.id"
                  class="border border-slate-200 dark:border-slate-700 rounded-xl transition-all duration-200"
                  :class="{ 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/40': selectedPayment === option.id, 'hover:border-blue-300 dark:hover:border-blue-400': selectedPayment !== option.id }"
                >
                  <!-- Header Area -->
                  <div
                    class="flex items-center gap-3 p-4 cursor-pointer"
                    @click="selectPayment(option.id)"
                  >
                    <input
                      :id="`payment-${option.id}`"
                      :checked="selectedPayment === option.id"
                      :value="option.id"
                      type="radio"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 pointer-events-none"
                    >
                    <template v-if="option.leadIcon">
                      <img
                        :src="option.leadIcon.src"
                        :alt="option.leadIcon.alt"
                        class="h-10 w-auto max-w-[132px] shrink-0 object-contain opacity-95 sm:h-12"
                        :class="option.leadIcon.darkSrc ? 'dark:hidden' : ''"
                        loading="lazy"
                      >
                      <img
                        v-if="option.leadIcon.darkSrc"
                        :src="option.leadIcon.darkSrc"
                        :alt="option.leadIcon.alt"
                        class="hidden h-10 w-auto max-w-[132px] shrink-0 object-contain opacity-95 dark:block sm:h-12"
                        loading="lazy"
                      >
                    </template>
                    <div class="flex-1 min-w-0">
                      <label
                        :for="`payment-${option.id}`"
                        class="block text-sm font-medium text-slate-900 dark:text-slate-100 cursor-pointer pointer-events-none"
                      >
                        {{ option.name }}
                      </label>
                      <p class="text-sm text-slate-500 dark:text-slate-400 pointer-events-none">
                        {{ option.description }}
                      </p>
                      <p
                        v-if="paymentInstallmentHint(option.providerId)"
                        class="mt-1 text-sm text-[#0071e3] dark:text-blue-400 pointer-events-none"
                      >
                        {{ paymentInstallmentHint(option.providerId) }}
                      </p>
                      <div
                        v-if="option.icons?.length || option.tokenBadges?.length"
                        class="mt-3 flex flex-wrap items-center gap-2 pointer-events-none"
                      >
                        <img
                          v-for="icon in option.icons || []"
                          :key="`${option.id}-${icon.src}`"
                          :src="icon.src"
                          :alt="icon.alt"
                          class="h-5 w-auto object-contain opacity-90"
                          loading="lazy"
                        >
                        <span
                          v-for="badge in option.tokenBadges || []"
                          :key="`${option.id}-badge-${badge}`"
                          class="inline-flex items-center rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-[11px] font-semibold tracking-[0.08em] text-slate-700 dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-100"
                        >
                          {{ badge }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Accordion Body -->
                  <div 
                    v-if="selectedPayment === option.id" 
                    class="px-4 pb-4 animate-fadeIn"
                  >
                    <div
                      v-if="isCryptoSelected"
                      class="space-y-4"
                    >
                      <div
                        v-if="cryptoTestnetActive"
                        class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                      >
                        {{ t('checkout.payment.cryptoTestnetWarning') }}
                      </div>
                      <p class="text-sm text-slate-500 dark:text-slate-400">
                        {{ t('checkout.payment.cryptoNotice') }}
                      </p>
                      <div class="space-y-2">
                        <span class="block text-sm font-medium text-slate-700 dark:text-slate-200">
                          {{ t('checkout.payment.cryptoTokenLabel') }}
                        </span>
                        <div class="grid gap-3 sm:grid-cols-2">
                          <button
                            v-for="token in orderedCryptoTokens"
                            :key="token.symbol"
                            type="button"
                            class="rounded-xl border px-4 py-3 text-left transition"
                            :class="isCryptoTokenActive(token.symbol)
                              ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/40'
                              : 'border-slate-300 hover:border-blue-300 dark:border-slate-700 dark:hover:border-blue-400'"
                            @click="selectedCryptoToken = String(token.symbol || 'USDC').toUpperCase() as 'USDC' | 'EURC'"
                          >
                            <div class="font-semibold text-slate-900 dark:text-slate-100">
                              {{ token.symbol }} - {{ token.display_name }}
                            </div>
                          </button>
                        </div>
                      </div>
                      <div
                        v-if="selectedCryptoOption"
                        class="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-4 space-y-2"
                      >
                        <div class="flex items-start justify-between gap-4">
                          <span class="text-sm text-slate-500 dark:text-slate-400">{{ t('checkout.payment.cryptoNetwork') }}</span>
                          <span class="text-sm font-medium text-slate-900 dark:text-slate-100 text-right">{{ cryptoNetworkLabel }}</span>
                        </div>
                        <div class="flex items-start justify-between gap-4">
                          <span class="text-sm text-slate-500 dark:text-slate-400">{{ t('checkout.payment.cryptoAmount') }}</span>
                          <span class="text-sm font-medium text-slate-900 dark:text-slate-100 text-right">
                            {{ cryptoQuoteDisplay }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="paymentOptions.length === 0 && !paymentOptionsLoading"
                  class="text-sm text-slate-500 dark:text-slate-400"
                >
                  {{ t('checkout.payment.unavailable') }}
                </div>
                <div
                  v-if="paymentOptionsLoading"
                  class="text-sm text-slate-500 dark:text-slate-400"
                >
                  {{ t('checkout.payment.loading') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Legal Notices -->
          <div class="bg-white dark:bg-[#1b1b1b] rounded-2xl p-8 border border-slate-200 dark:border-slate-700 shadow-sm">
            <h2 class="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
              {{ t('checkout.legal.title') }}
            </h2>
            
            <div class="space-y-4">
              <div class="flex items-start">
                <input
                  id="terms"
                  v-model="form.acceptedTerms"
                  type="checkbox"
                  required
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded mt-1"
                >
                <label
                  for="terms"
                  class="ml-3 block text-sm text-slate-700 dark:text-slate-300"
                >
                  <template v-if="isGerman">
                    Ich habe die
                    <router-link
                      to="/de/agb"
                      class="text-blue-600 dark:text-blue-400 hover:underline"
                    >AGB</router-link>,
                    <router-link
                      to="/de/datenschutzerklaerung"
                      class="text-blue-600 dark:text-blue-400 hover:underline"
                    >Datenschutzerklärung</router-link>
                    und die
                    <router-link
                      to="/de/rueckgabe-und-erstattung"
                      class="text-blue-600 dark:text-blue-400 hover:underline"
                    >Widerrufsbelehrung</router-link>
                    gelesen und akzeptiert.
                  </template>
                  <template v-else>
                    I have read and accepted the
                    <router-link
                      to="/terms-and-conditions"
                      class="text-blue-600 dark:text-blue-400 hover:underline"
                    >terms and conditions</router-link>,
                    <router-link
                      to="/privacy-policy"
                      class="text-blue-600 dark:text-blue-400 hover:underline"
                    >privacy policy</router-link>,
                    and
                    <router-link
                      to="/returns-and-refunds"
                      class="text-blue-600 dark:text-blue-400 hover:underline"
                    >cancellation policy</router-link>.
                  </template>
                </label>
              </div>

              <div class="flex items-start">
                <input
                  id="newsletter"
                  v-model="form.newsletter"
                  type="checkbox"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded mt-1"
                >
                <label
                  for="newsletter"
                  class="ml-3 block text-sm text-slate-700 dark:text-slate-300"
                >
                  {{ t('checkout.newsletter') }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Summary Sidebar -->
        <div>
          <div class="space-y-6 2xl:sticky 2xl:top-24">
            <!-- Order Summary -->
            <div class="bg-white dark:bg-[#1b1b1b] rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                {{ t('checkout.summary.title') }}
              </h3>
              
              <div class="space-y-4">
                <div
                  v-for="item in cartItems"
                  :key="item.id"
                  class="rounded-xl border border-slate-200 dark:border-slate-700 p-3"
                >
                  <div
                    v-if="item.isBundle"
                    class="space-y-3"
                  >
                    <div class="flex gap-3">
                      <div class="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 dark:bg-[#121212]">
                        <img
                          v-if="item.image"
                          :src="item.image"
                          :alt="item.name"
                          class="w-12 h-12 object-cover"
                        >
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="flex flex-col gap-3 min-[430px]:flex-row min-[430px]:items-start min-[430px]:justify-between">
                          <div class="min-w-0 flex-1">
                            <p class="text-sm font-medium text-slate-900 dark:text-slate-100 break-words">
                              {{ item.name }}
                            </p>
                            <p class="text-sm text-slate-500 dark:text-slate-400 break-words">
                              {{ item.variant }}
                            </p>
                            <button
                              class="mt-1 text-sm text-[#0071e3] hover:underline"
                              @click="toggleSummaryDetails(item.id)"
                            >
                              {{ isSummaryExpanded(item.id) ? t('cart.less') : t('cart.more') }}
                            </button>
                          </div>
                          <div class="flex shrink-0 items-center justify-end gap-3 min-w-[136px]">
                            <div class="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg">
                              <button
                                :disabled="updatingItems.has(item.id)"
                                class="px-2 py-1 text-slate-600 dark:text-slate-400"
                                @click="updateCheckoutBundleQuantity(item, item.quantity - 1)"
                              >
                                −
                              </button>
                              <input
                                :value="item.quantity"
                                readonly
                                class="w-8 text-center bg-transparent text-sm text-slate-900 dark:text-slate-100"
                              >
                              <button
                                :disabled="updatingItems.has(item.id)"
                                class="px-2 py-1 text-slate-600 dark:text-slate-400"
                                @click="updateCheckoutBundleQuantity(item, item.quantity + 1)"
                              >
                                +
                              </button>
                            </div>
                            <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {{ formatPrice(item.price) }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="isSummaryExpanded(item.id)"
                      class="space-y-3 pl-2"
                    >
                      <div
                        v-for="module in item.modules"
                        :key="`${item.id}-${module.id}`"
                        class="rounded-lg border border-slate-200 dark:border-slate-700 p-3"
                      >
                        <div class="flex gap-3">
                          <div class="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 dark:bg-[#121212]">
                            <img
                              v-if="module.image"
                              :src="module.image"
                              :alt="module.title"
                              class="w-10 h-10 object-cover"
                            >
                          </div>
                          <div class="flex-1 min-w-0">
                            <div class="flex flex-col gap-3 min-[430px]:flex-row min-[430px]:items-start min-[430px]:justify-between">
                              <div class="min-w-0 flex-1">
                                <p class="text-sm font-medium text-slate-900 dark:text-slate-100 break-words">
                                  {{ module.title }}
                                </p>
                                <button
                                  v-if="module.components.length || module.description"
                                  class="mt-1 text-sm text-[#0071e3] hover:underline"
                                  @click="toggleSummaryModule(item.id, module.id)"
                                >
                                  {{ isSummaryModuleExpanded(item.id, module.id) ? t('cart.less') : t('cart.more') }}
                                </button>
                              </div>
                              <div class="flex shrink-0 items-center justify-end gap-2 min-w-[96px]">
                                <div class="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg">
                                  <button
                                    :disabled="updatingItems.has(`${item.id}:${module.id}`)"
                                    class="px-2 py-1 text-slate-600 dark:text-slate-400"
                                    @click="updateCheckoutModuleQuantity(item, module, module.quantity - 1)"
                                  >
                                    −
                                  </button>
                                  <input
                                    :value="module.quantity"
                                    readonly
                                    class="w-8 text-center bg-transparent text-sm text-slate-900 dark:text-slate-100"
                                  >
                                  <button
                                    :disabled="updatingItems.has(`${item.id}:${module.id}`)"
                                    class="px-2 py-1 text-slate-600 dark:text-slate-400"
                                    @click="updateCheckoutModuleQuantity(item, module, module.quantity + 1)"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>

                            <p
                              v-if="module.description && isSummaryModuleExpanded(item.id, module.id)"
                              class="mt-2 text-sm text-slate-500 dark:text-slate-400 break-words"
                            >
                              {{ module.description }}
                            </p>

                            <div
                              v-if="module.components.length && isSummaryModuleExpanded(item.id, module.id)"
                              class="mt-3 space-y-3"
                            >
                              <div
                                v-for="component in module.components"
                                :key="component.id"
                                class="flex gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3"
                              >
                                <div class="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 dark:bg-[#121212]">
                                  <img
                                    v-if="component.image"
                                    :src="component.image"
                                    :alt="component.title"
                                    class="w-10 h-10 object-cover"
                                  >
                                </div>
                                <div class="flex-1 min-w-0">
                                  <div class="flex flex-col gap-3 min-[430px]:flex-row min-[430px]:items-start min-[430px]:justify-between">
                                    <div class="min-w-0 flex-1">
                                      <p class="text-sm font-medium text-slate-900 dark:text-slate-100 break-words">
                                        {{ component.title }}
                                      </p>
                                      <p
                                        v-if="component.variant"
                                        class="text-sm text-slate-500 dark:text-slate-400 break-words"
                                      >
                                        {{ component.variant }}
                                      </p>
                                    </div>
                                    <div class="flex shrink-0 items-center border border-slate-200 dark:border-slate-700 rounded-lg">
                                      <button
                                        :disabled="updatingItems.has(component.lineItemId)"
                                        class="px-2 py-1 text-slate-600 dark:text-slate-400"
                                        @click="updateCheckoutComponentQuantity(item, module, component, component.quantity - 1)"
                                      >
                                        −
                                      </button>
                                      <input
                                        :value="component.quantity"
                                        readonly
                                        class="w-8 text-center bg-transparent text-sm text-slate-900 dark:text-slate-100"
                                      >
                                      <button
                                        :disabled="updatingItems.has(component.lineItemId)"
                                        class="px-2 py-1 text-slate-600 dark:text-slate-400"
                                        @click="updateCheckoutComponentQuantity(item, module, component, component.quantity + 1)"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-else
                    class="flex gap-3"
                  >
                    <div class="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 dark:bg-[#121212]">
                      <img
                        v-if="item.image"
                        :src="item.image"
                        :alt="item.name"
                        class="w-12 h-12 object-cover"
                      >
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex flex-col gap-3 min-[430px]:flex-row min-[430px]:items-start min-[430px]:justify-between">
                        <div class="min-w-0 flex-1">
                          <p class="text-sm font-medium text-slate-900 dark:text-slate-100 break-words">
                            {{ item.name }}
                          </p>
                          <p class="text-sm text-slate-500 dark:text-slate-400 break-words">
                            {{ item.variant }}
                          </p>
                        </div>
                        <div class="flex shrink-0 items-center justify-end gap-3 min-w-[136px]">
                          <div class="flex items-center border border-slate-200 dark:border-slate-700 rounded-lg">
                            <button
                              :disabled="updatingItems.has(item.id)"
                              class="px-2 py-1 text-slate-600 dark:text-slate-400"
                              @click="updateCheckoutLineQuantity(item.id, item.quantity - 1)"
                            >
                              −
                            </button>
                            <input
                              :value="item.quantity"
                              readonly
                              class="w-8 text-center bg-transparent text-sm text-slate-900 dark:text-slate-100"
                            >
                            <button
                              :disabled="updatingItems.has(item.id)"
                              class="px-2 py-1 text-slate-600 dark:text-slate-400"
                              @click="updateCheckoutLineQuantity(item.id, item.quantity + 1)"
                            >
                              +
                            </button>
                          </div>
                          <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {{ formatPrice(item.price * item.quantity) }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4 space-y-2">
                <div class="flex justify-between text-sm">
                  <span class="text-slate-600 dark:text-slate-400">{{ t('checkout.summary.subtotal') }}</span>
                  <span class="text-slate-900 dark:text-slate-100">{{ formatPrice(subtotal) }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-slate-600 dark:text-slate-400">{{ t('checkout.summary.shipping') }}</span>
                  <span class="text-slate-900 dark:text-slate-100">
                    {{ shippingTotal === 0 ? t('checkout.free') : formatPrice(shippingTotal) }}
                  </span>
                </div>
                <div
                  v-if="paymentFee > 0"
                  class="flex justify-between text-sm"
                >
                  <span class="text-slate-600 dark:text-slate-400">{{ t('checkout.summary.paymentFee') }}</span>
                  <span class="text-slate-900 dark:text-slate-100">
                    +{{ formatPrice(paymentFee) }}
                  </span>
                </div>
                <div
                  v-if="taxTotal > 0"
                  class="flex justify-between text-sm"
                >
                  <span class="text-slate-600 dark:text-slate-400">{{ t('checkout.summary.tax') }}</span>
                  <span class="text-slate-900 dark:text-slate-100">{{ formatPrice(taxTotal) }}</span>
                </div>
                <div class="flex justify-between text-lg font-semibold pt-2 border-t border-slate-200 dark:border-slate-700">
                  <span class="text-slate-900 dark:text-slate-100">{{ t('checkout.summary.total') }}</span>
                  <span class="text-slate-900 dark:text-slate-100">{{ formatPrice(total) }}</span>
                </div>
              </div>
            </div>

            <!-- Environmental Contribution -->
            <div class="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900/60 rounded-xl p-4">
              <div class="flex items-center space-x-2 mb-2">
                <div class="w-2 h-2 bg-green-500 rounded-full" />
                <span class="text-sm font-medium text-green-800 dark:text-green-200">{{ t('checkout.environment.title') }}</span>
              </div>
              <p class="text-sm text-green-700 dark:text-green-200">
                {{ t('checkout.environment.text') }}
              </p>
            </div>

            <!-- Order Button -->
            <button
              data-testid="submit-order"
              :disabled="!isFormValid || isSubmitting"
              class="w-full bg-blue-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              @click="submitOrder"
            >
              <span
                v-if="isSubmitting"
                class="flex items-center justify-center"
              >
                <div class="spinner mr-2" />
                {{ t('checkout.submitting') }}
              </span>
              <span v-else>{{ t('checkout.submit') }}</span>
            </button>
            <p
              v-if="errorMessage"
              class="text-sm text-red-600 dark:text-red-400 text-center"
            >
              {{ errorMessage }}
            </p>

            <!-- Security Badges -->
            <div class="flex items-center justify-center space-x-4 text-xs text-slate-500 dark:text-slate-400">
              <div class="flex items-center space-x-1">
                <svg
                  class="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>{{ t('checkout.securePayment') }}</span>
              </div>
              <div class="flex items-center space-x-1">
                <svg
                  class="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span>{{ t('checkout.ssl') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cashpresso Modal -->
    <div
      v-if="cashpressoModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6"
    >
      <div class="w-full max-w-xl rounded-2xl bg-white dark:bg-[#1b1b1b] border border-slate-200 dark:border-slate-700 shadow-xl">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Cashpresso
          </h3>
          <button
            class="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white"
            @click="closeCashpressoModal"
          >
            {{ t('checkout.back') }}
          </button>
        </div>
        <div class="p-6 space-y-4">
          <div
            id="cashpresso-checkout"
            :key="cashpressoWidgetKey"
            class="min-h-[120px]"
          />
          <input
            id="cashpressoToken"
            type="hidden"
          >
          <p
            v-if="cashpressoStatus === 'loading'"
            class="text-sm text-slate-500 dark:text-slate-400"
          >
            {{ t('checkout.payment.cashpressoLoading') }}
          </p>
          <p
            v-else-if="cashpressoStatus === 'error'"
            class="text-sm text-red-600 dark:text-red-400"
          >
            {{ cashpressoError }}
          </p>
          <p
            v-else-if="cashpressoStatus === 'idle'"
            class="text-sm text-slate-500 dark:text-slate-400"
          >
            {{ cashpressoHint }}
          </p>
        </div>
      </div>
    </div>

    <!-- Alma Modal -->
    <div
      v-if="almaModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6"
    >
      <div class="w-full max-w-xl max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-[#1b1b1b] border border-slate-200 dark:border-slate-700 shadow-xl flex flex-col">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-[#1b1b1b] shrink-0">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Alma</h3>
          <button
            class="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white"
            @click="closeAlmaModal"
          >
            {{ t('checkout.back') }}
          </button>
        </div>
        <div class="p-6 space-y-4 overflow-y-auto min-h-0">
          <div id="alma-in-page" class="min-h-[120px]" />
          <p v-if="almaStatus === 'loading'" class="text-sm text-slate-500 dark:text-slate-400">
            {{ t('checkout.payment.almaLoading') }}
          </p>
          <p v-else-if="almaStatus === 'error'" class="text-sm text-red-600 dark:text-red-400">
            {{ almaError || t('checkout.payment.almaUnavailable') }}
          </p>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { resolveApiBase } from '@/utils/api-base'
import { storeToRefs } from 'pinia'
import { useCartStore } from '@/stores/cart'
import { useMedusaStore } from '@/stores/medusa'
import { useTranslationsStore } from '@/stores/translations'
import {
  COUNTRIES,
  toBackendCountryCode,
  toDisplayCountryCode,
} from '@/config/region-config'
import { getConsentState, isConsentGranted, registerNecessaryCookie } from '@/lib/consent'
import {
  buildReadyKitBundleEntry,
  countReadyKitBundleModules,
  getReadyKitBundleBaseQuantity,
  groupReadyKitCartEntries,
} from '@/utils/configuration-bundle'
import { evaluateStructuralCartRule } from '@/utils/structural-cart'

const router = useRouter()
const cartStore = useCartStore()
const medusaStore = useMedusaStore()
const translationsStore = useTranslationsStore()
const { cart } = storeToRefs(cartStore)
const { t, locale } = useI18n()
const isGerman = computed(() => locale.value === 'de')
const publishableKey = import.meta.env.VITE_MEDUSA_PUBLISHABLE_KEY as string | undefined
const apiBase = resolveApiBase(import.meta.env.VITE_MEDUSA_BACKEND_URL || import.meta.env.VITE_MEDUSA_API_URL)
type PublicPaymentModulesConfig = {
  paypal: { client_id: string | null; environment: string | null }
  alma: { merchant_id: string | null; environment: string | null; min_amount_cents: number; max_amount_cents: number }
  cashpresso: { partner_id: string | null; api_key: string | null; environment: string | null; interest_free_days: number }
  stablecoin: { environment: string | null; network_label: string | null; testnet_active: boolean; warning: string | null }
}
const paymentModulesConfig = ref<PublicPaymentModulesConfig>({
  paypal: {
    client_id: (import.meta.env.VITE_PAYPAL_CLIENT_ID as string | undefined) || null,
    environment: 'sandbox',
  },
  alma: {
    merchant_id: (import.meta.env.VITE_ALMAPAY_MERCHANT_ID as string | undefined) || null,
    environment: (import.meta.env.VITE_ALMAPAY_ENVIRONMENT as string | undefined) || 'sandbox',
    min_amount_cents: 5000,
    max_amount_cents: 200000,
  },
  cashpresso: {
    partner_id: (import.meta.env.VITE_CASHPRESSO_PARTNER_ID as string | undefined) || null,
    api_key: (import.meta.env.VITE_CASHPRESSO_API_KEY as string | undefined) || null,
    environment: (import.meta.env.VITE_CASHPRESSO_MODE as string | undefined) || 'live',
    interest_free_days: Number(import.meta.env.VITE_CASHPRESSO_INTEREST_FREE_DAYS || 0),
  },
  stablecoin: {
    environment: 'testnet',
    network_label: null,
    testnet_active: false,
    warning: null,
  },
})
const paypalClientId = computed(() => String(paymentModulesConfig.value.paypal?.client_id || '').trim())
const almaMerchantId = computed(() => String(paymentModulesConfig.value.alma?.merchant_id || '').trim())
const almaEnvironmentEnv = computed(() => String(paymentModulesConfig.value.alma?.environment || 'sandbox').trim())
const cashpressoPartnerId = computed(() => String(paymentModulesConfig.value.cashpresso?.partner_id || '').trim())
const cashpressoApiKey = computed(() => String(paymentModulesConfig.value.cashpresso?.api_key || '').trim())
const cashpressoModeEnv = computed(() => String(paymentModulesConfig.value.cashpresso?.environment || 'live').trim())
const cashpressoInterestFreeDays = computed(() => Number(paymentModulesConfig.value.cashpresso?.interest_free_days || 0))
const legalTermsVersion = import.meta.env.VITE_LEGAL_TERMS_VERSION as string | undefined
const legalPrivacyVersion = import.meta.env.VITE_LEGAL_PRIVACY_VERSION as string | undefined
const legalWithdrawalVersion = import.meta.env.VITE_LEGAL_WITHDRAWAL_VERSION as string | undefined
const ALMA_MIN_AMOUNT = computed(() => Number(paymentModulesConfig.value.alma?.min_amount_cents || 5000))
const ALMA_MAX_AMOUNT = computed(() => Number(paymentModulesConfig.value.alma?.max_amount_cents || 200000))

// Form data
const form = ref({
  firstName: '',
  lastName: '',
  company: '',
  address: '',
  postalCode: '',
  city: '',
  country: 'AT',
  phone: '',
  email: '',
  vatId: '',
  acceptedTerms: false,
  newsletter: false
})

const billing = ref({
  firstName: '',
  lastName: '',
  company: '',
  address: '',
  postalCode: '',
  city: '',
  country: 'AT'
})

const isB2BCustomer = ref(false)
const billingSameAsShipping = ref(true)
const selectedShipping = ref<string | null>(null)
const selectedPayment = ref<string | null>(null)
const isSubmitting = ref(false)
const errorMessage = ref('')
const shippingOptionsLoading = ref(false)
const paymentOptionsLoading = ref(false)
const vatStatus = ref<'idle' | 'checking' | 'valid' | 'invalid' | 'mismatch' | 'error'>('idle')
const vatDetails = ref<any>(null)
const cashpressoStatus = ref<'idle' | 'loading' | 'ready' | 'error'>('idle')
const cashpressoError = ref('')
const cashpressoToken = ref('')
const cashpressoPaymentInfo = ref<any>(null)
const cashpressoWidgetKey = ref(0)
const cashpressoModalOpen = ref(false)
const cashpressoSubmitRequested = ref(false)
const almaStatus = ref<'idle' | 'loading' | 'ready' | 'approved' | 'error'>('idle')
const almaError = ref('')
const almaPaymentId = ref('')
const almaModalOpen = ref(false)
const almaSuccessHandled = ref(false)
const almaQuote = ref<{ monthlyCents: number; duration: number; totalCents?: number } | null>(null)
const cashpressoQuote = ref<{ monthlyCents: number; duration: number; totalCents?: number; interestRate?: number } | null>(null)
const cryptoAvailability = ref<any | null>(null)
const cryptoQuote = ref<any | null>(null)
const selectedCryptoToken = ref<'USDC' | 'EURC'>('USDC')
const pendingCryptoPublicToken = ref('')
const updatingItems = ref(new Set<string>())
const expandedSummaryDetails = ref(new Set<string>())
const expandedSummaryModules = ref(new Set<string>())
let almaScriptPromise: Promise<void> | null = null
const paypalStatus = ref<'idle' | 'loading' | 'ready' | 'approved' | 'error'>('idle')
const paypalError = ref('')
const paypalCapture = ref<any>(null)
const paypalRedirectInFlight = ref(false)
const paypalReturnInProgress = ref(false)
let lastInitializedPaymentOption: string | null = null
let vatTimer: number | null = null
let vatRequestId = 0
let cashpressoInitTimer: number | null = null
let cashpressoQuoteTimer: number | null = null
let almaQuoteTimer: number | null = null

const shippingOptions = ref<Array<{ id: string; name: string; description: string; price: number }>>([])
type PaymentOptionIcon = { src: string; alt: string; darkSrc?: string }
type CheckoutPaymentOption = {
  id: string
  providerId: string
  name: string
  description: string
  fee: number
  priority: number
  leadIcon?: PaymentOptionIcon | null
  icons?: PaymentOptionIcon[]
  tokenBadges?: string[]
}

const paymentOptions = ref<CheckoutPaymentOption[]>([])
const selectedPaymentOption = computed(() =>
  paymentOptions.value.find((option) => option.id === selectedPayment.value)
)

const goBackToCart = () => {
  const base = router.currentRoute.value.path.startsWith('/de') ? '/de/cart' : '/cart'
  router.push(base)
}
const isPayPalSelected = computed(() =>
  String(selectedPaymentOption.value?.providerId || '').toLowerCase().includes('paypal')
)
const isAlmaSelected = computed(() =>
  String(selectedPaymentOption.value?.providerId || '').toLowerCase().includes('alma')
)
const isCryptoSelected = computed(() =>
  String(selectedPaymentOption.value?.providerId || '').toLowerCase().includes('crypto')
)
const isPayPalAccountSelected = computed(() => String(selectedPayment.value || '').includes('-paypal'))
const selectedCountry = ref('DE')
const lastRegionSync = ref('')
const addressSuggestions = ref<Array<{
  label: string
  street: string
  housenumber: string
  postcode: string
  city: string
  countryCode: string
}>>([])
const addressStatus = ref<'idle' | 'loading' | 'empty' | 'error'>('idle')
const addressFocused = ref(false)
let addressSearchTimer: number | null = null
let addressAbort: AbortController | null = null

const isAddressReady = computed(() => {
  return !!(
    form.value.firstName &&
    form.value.lastName &&
    form.value.address &&
    form.value.postalCode &&
    form.value.city &&
    form.value.phone &&
    form.value.country
  )
})

const cashpressoMode = computed(() => {
  const normalized = String(cashpressoModeEnv.value || 'live').toLowerCase()
  return ['test', 'sandbox', 'sim', 'simulation'].includes(normalized) ? 'test' : 'live'
})

const almaEnvironment = computed(() => {
  const normalized = String(almaEnvironmentEnv.value || 'sandbox').toLowerCase()
  return normalized === 'production' || normalized === 'live' ? 'LIVE' : 'TEST'
})

const almaCheckoutOrigin = computed(() =>
  almaEnvironment.value === 'LIVE' ? 'https://checkout.getalma.eu' : 'https://checkout.sandbox.getalma.eu'
)

const isAlmaOrigin = (origin: string) => {
  if (!origin) return false
  if (origin === almaCheckoutOrigin.value) return true
  return /https:\/\/checkout(\.sandbox)?\.getalma\.eu$/i.test(origin) ||
    /https:\/\/checkout\.(staging|dev)\.almapay\.com$/i.test(origin)
}

const countryOptions = computed(() => {
  const lang = locale.value === 'de' ? 'de' : 'en'
  return COUNTRIES.map((country) => ({
    code: country.code,
    label: country.labels[lang as 'de' | 'en'] || country.labels.de,
  })).sort((a, b) => a.label.localeCompare(b.label, lang))
})

const addressLoadingText = computed(() => t('checkout.address.loading'))

const addressHint = computed(() => {
  if (addressStatus.value === 'empty') {
    return t('checkout.address.empty')
  }
  if (addressStatus.value === 'error') {
    return t('checkout.address.unavailable')
  }
  return ''
})

const cashpressoAmountCents = computed(() => {
  const amount = cart.value?.total ?? cart.value?.subtotal ?? 0
  return amount > 0 ? amount : 0
})

const almaAmountCents = computed(() => {
  const amount = cart.value?.total ?? cart.value?.subtotal ?? 0
  if (amount > 0) return amount
  const items = cart.value?.items || []
  const itemsTotal = items.reduce((sum, item) => {
    const unit = Number(item?.unit_price || 0)
    const qty = Number(item?.quantity || 0)
    return sum + unit * qty
  }, 0)
  return itemsTotal > 0 ? itemsTotal : 0
})

const normalizeCashpressoQuote = (data: any) => {
  const plan =
    data?.currentPlan ||
    data?.plan ||
    data?.paymentPlan ||
    (Array.isArray(data?.plans) ? data.plans[0] : null) ||
    data

  const numberValue = (value: any) => {
    if (value === null || value === undefined) return null
    const normalized = String(value).replace(',', '.').replace(/[^0-9.]/g, '').trim()
    if (!normalized) return null
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : null
  }

  const moneyToCents = (value: any) => {
    const parsed = numberValue(value)
    return parsed === null ? null : Math.round(parsed * 100)
  }

  const monthlyCents = moneyToCents(
    plan?.instalmentAmount ??
      plan?.instalment_amount ??
      plan?.monthlyAmount ??
      plan?.monthly_amount ??
      data?.instalmentAmount ??
      data?.instalment_amount
  )
  const duration = numberValue(plan?.duration ?? plan?.term ?? plan?.months ?? data?.duration ?? data?.term ?? data?.months)

  if (!monthlyCents || !duration) return null

  return {
    monthlyCents,
    duration: Math.round(duration),
    totalCents:
      moneyToCents(plan?.totalAmount ?? plan?.total_amount ?? plan?.amount ?? data?.totalAmount ?? data?.total_amount) ||
      undefined,
    interestRate:
      numberValue(plan?.interestRate ?? plan?.interest_rate ?? data?.interestRate ?? data?.interest_rate) || undefined,
  }
}

const normalizeAlmaQuote = (data: any) => {
  if (!data?.eligible) return null
  const duration = Number(data?.installments_count || 0)
  const paymentPlan = Array.isArray(data?.payment_plan) ? data.payment_plan : []
  const firstInstallment = paymentPlan[0]
  const monthlyCents = Number(firstInstallment?.total_amount ?? firstInstallment?.purchase_amount ?? 0)

  if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(monthlyCents) || monthlyCents <= 0) {
    return null
  }

  const totalCents = paymentPlan.reduce((sum: number, step: any) => {
    const amount = Number(step?.total_amount ?? step?.purchase_amount ?? 0)
    return sum + (Number.isFinite(amount) ? amount : 0)
  }, 0)

  return {
    monthlyCents: Math.round(monthlyCents),
    duration: Math.round(duration),
    totalCents: totalCents > 0 ? Math.round(totalCents) : undefined,
  }
}

const availableCryptoTokens = computed(() =>
  Array.isArray(cryptoAvailability.value?.options) ? cryptoAvailability.value.options : []
)

const cryptoTestnetActive = computed(() => Boolean(cryptoAvailability.value?.testnet_active))

const networkLabelFromChainId = (chainId: unknown) => {
  const numeric = Number(chainId || 1)
  return numeric === 11155111 ? 'Ethereum Sepolia' : 'Ethereum Mainnet'
}

const cryptoNetworkLabel = computed(() =>
  String(
    cryptoQuote.value?.network_label ||
      cryptoAvailability.value?.network_label ||
      (selectedCryptoOption.value ? networkLabelFromChainId(selectedCryptoOption.value.chain_id) : networkLabelFromChainId(1))
  )
)

const paymentIcon = (name: string) => `/payment-icons/${name}`
const paymentLeadIcon = (name: string) => ({ src: paymentIcon(name), alt: name })

const cardBrandIcons: PaymentOptionIcon[] = [
  { src: paymentIcon('visa.svg'), alt: 'Visa' },
  { src: paymentIcon('mastercard.svg'), alt: 'Mastercard' },
  { src: paymentIcon('american-express.svg'), alt: 'American Express' },
]

const paypalLeadIcon: PaymentOptionIcon = { src: paymentIcon('paypal-brand.svg'), alt: 'PayPal' }
const almaLeadIcon: PaymentOptionIcon = {
  src: paymentIcon('alma-black.svg'),
  darkSrc: paymentIcon('alma-white.svg'),
  alt: 'Alma',
}
const cashpressoLeadIcon: PaymentOptionIcon = { src: paymentIcon('cashpresso.svg'), alt: 'Cashpresso' }
const cryptoIcons: PaymentOptionIcon[] = [{ src: paymentIcon('ethereum.svg'), alt: 'Ethereum' }]

const selectedCryptoOption = computed(() =>
  availableCryptoTokens.value.find((entry: any) => String(entry.symbol || '').toUpperCase() === selectedCryptoToken.value) || null
)
const orderedCryptoTokens = computed(() => {
  const preferredOrder = ["USDC", "EURC"]
  return [...availableCryptoTokens.value].sort((left: any, right: any) => {
    const leftIndex = preferredOrder.indexOf(String(left?.symbol || "").toUpperCase())
    const rightIndex = preferredOrder.indexOf(String(right?.symbol || "").toUpperCase())
    return (leftIndex === -1 ? 999 : leftIndex) - (rightIndex === -1 ? 999 : rightIndex)
  })
})
const isCryptoTokenActive = (symbol: string) => String(symbol || "").toUpperCase() === selectedCryptoToken.value

const cryptoQuoteDisplay = computed(() => {
  if (cryptoQuote.value?.target_amount_display && cryptoQuote.value?.selected_token) {
    return `${cryptoQuote.value.target_amount_display} ${cryptoQuote.value.selected_token}`
  }
  if (selectedCryptoOption.value) {
    return `-- ${selectedCryptoOption.value.symbol}`
  }
  return '--'
})

const paymentInstallmentHint = (providerId?: string | null) => {
  const id = String(providerId || '').toLowerCase()
  if (id.includes('alma') && almaQuote.value) {
    return `${t('hero.from')} ${formatPrice(almaQuote.value.monthlyCents)}`
  }
  if (id.includes('cashpresso') && cashpressoQuote.value) {
    return `${t('hero.from')} ${formatPrice(cashpressoQuote.value.monthlyCents)}`
  }
  return ''
}

const loadCryptoAvailability = async () => {
  const payload = await cartStore.fetchCryptoAvailability()
  cryptoAvailability.value = payload
  const options = Array.isArray(payload?.options) ? payload.options : []
  if (options.length) {
    const current = options.find((entry: any) => String(entry.symbol || '').toUpperCase() === selectedCryptoToken.value)
    if (!current) {
      selectedCryptoToken.value = String(options[0].symbol || 'USDC').toUpperCase() as 'USDC' | 'EURC'
    }
  }
  return payload
}

const buildCryptoSessionData = (request: any) => {
  if (!request) return {}
  return {
    crypto_payment_request_id: request.id,
    selected_token: request.selected_token,
    crypto_payment_public_token: request.public_token,
    target_currency_code: request.target_currency_code,
    target_amount_atomic: request.target_amount_atomic,
    target_amount_display: request.target_amount_display,
    chain_id: request.chain_id,
    status: request.status,
  }
}

const prepareCryptoQuote = async (persist = false) => {
  if (!cart.value?.id) return null
  const payload = await cartStore.createCryptoQuote(selectedCryptoToken.value, cart.value.id, persist)
  const request = payload?.request || payload?.quote || null
  if (!request) return null

  cryptoAvailability.value = payload?.availability || cryptoAvailability.value
  cryptoQuote.value = request
  if (!persist) {
    pendingCryptoPublicToken.value = ''
    return request
  }

  pendingCryptoPublicToken.value = String(request.public_token || '')
  const metadata = {
    ...(cart.value.metadata || {}),
    crypto_payment_request_id: request.id,
    crypto_payment_public_token: request.public_token,
    crypto_selected_token: request.selected_token,
    crypto_target_amount_display: request.target_amount_display,
    crypto_target_currency_code: request.target_currency_code,
  }
  await cartStore.updateCart({ metadata })
  return request
}

const cashpressoHint = computed(() => {
  if (!cashpressoPartnerId.value || !cashpressoApiKey.value) {
    return t('checkout.cashpresso.notConfigured')
  }
  if (!isAddressReady.value) {
    return t('checkout.cashpresso.addressMissing')
  }
  if (!cashpressoAmountCents.value) {
    return t('checkout.cashpresso.noAmount')
  }
  return ''
})

const cashpressoReadyForInit = computed(() => {
  return Boolean(
      cashpressoModalOpen.value &&
      isCashpressoSelected.value &&
      cashpressoPartnerId.value &&
      cashpressoApiKey.value &&
      isAddressReady.value &&
      cashpressoAmountCents.value > 0
  )
})

const cashpressoReadyToStart = computed(() => {
  return Boolean(
    isCashpressoSelected.value &&
    cashpressoPartnerId.value &&
    cashpressoApiKey.value &&
    isAddressReady.value &&
    cashpressoAmountCents.value > 0
  )
})

// VAT Mismatch Fields
const vatMismatchFields = computed(() => {
  if (vatStatus.value !== 'mismatch' || !vatDetails.value) return []
  const fields: string[] = []
  const matches = vatDetails.value.matches || {}
  const nameChecks = vatDetails.value.name_checks || {}
  const isInvalid = (val: string) => val === 'INVALID' 
  
  // Dynamische Namen ohne Klammern
  if (isInvalid(matches.name) || (nameChecks.company === false && nameChecks.person === false)) {
    fields.push(t('checkout.vat.fields.company'))
  }
  if (isInvalid(matches.street)) {
    fields.push(t('checkout.vat.fields.street'))
  }
  if (isInvalid(matches.postal_code)) {
    fields.push(t('checkout.vat.fields.postalCode'))
  }
  if (isInvalid(matches.city)) {
    fields.push(t('checkout.vat.fields.city'))
  }
  return fields
})

const vatMessage = computed(() => {
  switch (vatStatus.value) {
    case 'checking':
      return t('checkout.vat.checking')
    case 'valid':
      return t('checkout.vat.valid')
    case 'mismatch': {
      const fields = vatMismatchFields.value
      // User Wunsch: "Bitte {plz}, {Stadt} prüfen"
      if (fields.length > 0) {
        const fieldList = fields.join(', ')
        return t('checkout.vat.mismatchFields', { fields: fieldList })
      }
      return t('checkout.vat.mismatch')
    }
    case 'invalid':
      return t('checkout.vat.invalid')
    case 'error':
      return t('checkout.vat.unavailable')
    default:
      return ''
  }
})

// Payment Computed

const isCashpressoSelected = computed(() =>
  String(selectedPaymentOption.value?.providerId || '').toLowerCase().includes('cashpresso')
)
const almaEligible = computed(() =>
  paymentOptions.value.some((option) => String(option.providerId || '').toLowerCase().includes('alma'))
)
const cashpressoEligible = computed(() =>
  paymentOptions.value.some((option) => String(option.providerId || '').toLowerCase().includes('cashpresso'))
)

const vatMessageClass = computed(() => {
  if (vatStatus.value === 'valid') return 'text-green-700'
  if (vatStatus.value === 'mismatch') return 'text-amber-700'
  if (vatStatus.value === 'invalid' || vatStatus.value === 'error') return 'text-red-600'
  return 'text-slate-500'
})

// Computed properties
const resolveVariantImage = (variant: any): string => {
  if (!variant) return ''
  const variantImage = Array.isArray(variant.images) ? variant.images[0]?.url : ''
  return variantImage || ''
}

const bundleContext = computed(() => ({
  backpackProductId: medusaStore.primaryBackpack?.id || null,
  moduleProductIds: new Set(medusaStore.modules.map((module) => String(module.id))),
}))

const structuralRule = computed(() =>
  evaluateStructuralCartRule(cart.value?.items || [], {
    productMap: medusaStore.productMap,
    moduleProductIds: bundleContext.value.moduleProductIds,
    backpackProductId: bundleContext.value.backpackProductId,
  })
)

const resolveCheckoutProduct = (item: any) => {
  const productId = String(item?.product_id || item?.product?.id || item?.variant?.product_id || '')
  return medusaStore.productMap?.[productId] || item?.product || null
}

const getCheckoutProductById = (productId?: string | null) => {
  if (!productId) return null
  return medusaStore.productMap?.[String(productId)] || null
}

const getCheckoutItemTitle = (item: any) => {
  const currentLocale = locale.value?.split('-')[0] || 'de'
  const fallback = item?.title || ''
  return translationsStore.getResolvedProductField(resolveCheckoutProduct(item) || { id: item?.product_id }, 'title', currentLocale, fallback)
}

const getCheckoutItemVariantTitle = (item: any) => {
  const fallback = item?.variant_title || item?.variant?.title || ''
  if (!fallback || fallback === 'Default variant') return ''
  const currentLocale = locale.value?.split('-')[0] || 'de'
  const variantId = String(item?.variant_id || item?.variant?.id || '')
  if (!variantId) return fallback
  const translated = translationsStore.getResolvedVariantField(
    resolveCheckoutProduct(item) || { id: item?.product_id },
    variantId,
    currentLocale,
    fallback
  )
  return translated === 'Default variant' ? '' : translated
}

const getCheckoutItemDescription = (item: any) => {
  const fallback = item?.product_description || ''
  const currentLocale = locale.value?.split('-')[0] || 'de'
  return translationsStore.getResolvedProductField(resolveCheckoutProduct(item) || { id: item?.product_id }, 'description', currentLocale, fallback)
}

const getLocalizedCheckoutProductTitle = (productId?: string | null, fallback = '') => {
  const product = getCheckoutProductById(productId)
  if (!product) return fallback
  const currentLocale = locale.value?.split('-')[0] || 'de'
  return translationsStore.getResolvedProductField(product, 'title', currentLocale, fallback || product.title || '')
}

const getLocalizedCheckoutProductDescription = (productId?: string | null, fallback = '') => {
  const product = getCheckoutProductById(productId)
  if (!product) return fallback
  const currentLocale = locale.value?.split('-')[0] || 'de'
  return translationsStore.getResolvedProductField(product, 'description', currentLocale, fallback || product.description || '')
}

const getCheckoutProductImage = (productId?: string | null) => {
  const product = getCheckoutProductById(productId)
  if (!product) return ''
  return product.thumbnail || product.images?.[0]?.url || ''
}

const getSummaryBundleDetailKey = (bundleId: string) => `checkout-bundle:${bundleId}`
const getSummaryModuleDetailKey = (bundleId: string, moduleId: string) => `checkout-module:${bundleId}:${moduleId}`

const cartItems = computed(() => {
  const groupedEntries = groupReadyKitCartEntries(cart.value?.items || [], bundleContext.value)

  return groupedEntries.map((entry) => {
    if (!entry.isBundle) {
      const item = entry.items[0]
      return {
        id: item.id,
        name: getCheckoutItemTitle(item),
        variant: getCheckoutItemVariantTitle(item),
        price: item.unit_price ?? 0,
        quantity: item.quantity,
        image: resolveVariantImage(item?.variant),
        description: getCheckoutItemDescription(item),
      }
    }

    const bundleEntry = buildReadyKitBundleEntry(entry.id, entry.items, bundleContext.value)
    const backpackItem = bundleEntry.backpackItem
    const representativeItem = bundleEntry.rootItem || entry.items[0]
    const isModulesOnly = bundleEntry.mode === 'modules-only' || !backpackItem
    const moduleCountForBundle = countReadyKitBundleModules(entry.items, bundleContext.value)
    const variantParts = [
      backpackItem ? getCheckoutItemVariantTitle(backpackItem) : '',
      moduleCountForBundle ? t('common.selectedModules', { count: moduleCountForBundle }) : '',
    ].filter(Boolean)

    return {
      id: bundleEntry.id,
      isBundle: true,
      name: isModulesOnly ? t('common.configuredModules') : t('common.configuredBundle'),
      variant: variantParts.join(' • '),
      price: bundleEntry.items.reduce((sum, item) => sum + ((item.unit_price ?? 0) * (item.quantity || 0)), 0),
      quantity: bundleEntry.quantity,
      image: representativeItem ? resolveVariantImage(representativeItem?.variant) : '',
      lineItemIds: bundleEntry.lineItemIds,
      modules: bundleEntry.modules.map((module) => {
        const fallbackModuleTitle = String(module.items[0]?.metadata?.bundle_module_title || module.moduleId || '')
        return {
          id: module.id,
          title: getLocalizedCheckoutProductTitle(module.moduleProductId, fallbackModuleTitle),
          description: getLocalizedCheckoutProductDescription(module.moduleProductId, ''),
          image: getCheckoutProductImage(module.moduleProductId) || resolveVariantImage(module.items[0]?.variant),
          quantity: module.quantity,
          lineItemIds: module.lineItemIds,
          components: module.components.map((component) => ({
            id: component.id,
            lineItemId: component.id,
            title: getCheckoutItemTitle(component.item),
            variant: getCheckoutItemVariantTitle(component.item),
            description: getCheckoutItemDescription(component.item),
            image: resolveVariantImage(component.item?.variant),
            quantity: component.quantity,
            price: component.item?.unit_price ?? 0,
          })),
        }
      }),
    }
  })
})

const subtotal = computed(() => cart.value?.subtotal ?? 0)
const shippingTotal = computed(() => cart.value?.shipping_total ?? 0)
const taxTotal = computed(() => cart.value?.tax_total ?? 0)
const total = computed(() => cart.value?.total ?? 0)
const paymentFee = computed(() => 0)

const toggleSummaryDetails = (bundleId: string) => {
  const key = getSummaryBundleDetailKey(bundleId)
  if (expandedSummaryDetails.value.has(key)) {
    expandedSummaryDetails.value.delete(key)
  } else {
    expandedSummaryDetails.value.add(key)
  }
}

const isSummaryExpanded = (bundleId: string) => expandedSummaryDetails.value.has(getSummaryBundleDetailKey(bundleId))

const toggleSummaryModule = (bundleId: string, moduleId: string) => {
  const key = getSummaryModuleDetailKey(bundleId, moduleId)
  if (expandedSummaryModules.value.has(key)) {
    expandedSummaryModules.value.delete(key)
  } else {
    expandedSummaryModules.value.add(key)
  }
}

const isSummaryModuleExpanded = (bundleId: string, moduleId: string) => expandedSummaryModules.value.has(getSummaryModuleDetailKey(bundleId, moduleId))

const isFormValid = computed(() => {
  return form.value.firstName &&
         form.value.lastName &&
         form.value.address &&
         form.value.postalCode &&
         form.value.city &&
         form.value.phone &&
         form.value.email &&
         form.value.acceptedTerms &&
         !!selectedShipping.value &&
         !!selectedPayment.value
})

// Methods
const formatPrice = (price: number) => {
  const localeTag = locale.value === 'de' ? 'de-DE' : 'en-US'
  return new Intl.NumberFormat(localeTag, {
    style: 'currency',
    currency: cart.value?.currency_code?.toUpperCase() || 'EUR'
  }).format(price / 100)
}

const updateCashpressoQuote = async () => {
  if (!cashpressoEligible.value || !cashpressoAmountCents.value) {
    cashpressoQuote.value = null
    return
  }
  const quote = await cartStore.fetchCashpressoQuote(cashpressoAmountCents.value)
  cashpressoQuote.value = normalizeCashpressoQuote(quote)
}

const updateAlmaQuote = async () => {
  if (!almaEligible.value || almaAmountCents.value < ALMA_MIN_AMOUNT.value) {
    almaQuote.value = null
    return
  }
  const quote = await cartStore.fetchAlmaQuote(almaAmountCents.value)
  almaQuote.value = normalizeAlmaQuote(quote)
}

const scheduleCashpressoQuote = () => {
  if (typeof window === 'undefined') return
  if (cashpressoQuoteTimer) window.clearTimeout(cashpressoQuoteTimer)
  cashpressoQuoteTimer = window.setTimeout(() => {
    cashpressoQuoteTimer = null
    void updateCashpressoQuote()
  }, 250)
}

const scheduleAlmaQuote = () => {
  if (typeof window === 'undefined') return
  if (almaQuoteTimer) window.clearTimeout(almaQuoteTimer)
  almaQuoteTimer = window.setTimeout(() => {
    almaQuoteTimer = null
    void updateAlmaQuote()
  }, 250)
}

const updateCheckoutLineQuantity = async (lineItemId: string, newQuantity: number) => {
  if (updatingItems.value.has(lineItemId)) return
  updatingItems.value.add(lineItemId)
  try {
    if (newQuantity < 1) {
      await cartStore.removeItem(lineItemId)
    } else {
      await cartStore.updateItem(lineItemId, newQuantity)
    }
    await loadShippingOptions()
    await loadPaymentProviders()
  } catch (error) {
    console.error('Checkout item update failed:', error)
  } finally {
    updatingItems.value.delete(lineItemId)
  }
}

const updateCheckoutBundleQuantity = async (bundleItem: any, newQuantity: number) => {
  if (!bundleItem || updatingItems.value.has(bundleItem.id)) return
  updatingItems.value.add(bundleItem.id)
  try {
    if (newQuantity < 1) {
      for (const lineItemId of bundleItem.lineItemIds || []) {
        await cartStore.removeItem(lineItemId)
      }
    } else {
      const sourceItems = (cart.value?.items || []).filter((item: any) => bundleItem.lineItemIds.includes(item.id))
      for (const lineItem of sourceItems) {
        const baseQuantity = getReadyKitBundleBaseQuantity(lineItem)
        await cartStore.updateItem(lineItem.id, Math.max(1, baseQuantity * newQuantity))
      }
    }
    await loadShippingOptions()
    await loadPaymentProviders()
  } catch (error) {
    console.error('Checkout bundle update failed:', error)
  } finally {
    updatingItems.value.delete(bundleItem.id)
  }
}

const updateCheckoutModuleQuantity = async (bundleItem: any, moduleItem: any, newQuantity: number) => {
  const updateKey = `${bundleItem.id}:${moduleItem.id}`
  if (updatingItems.value.has(updateKey)) return
  updatingItems.value.add(updateKey)
  try {
    if (newQuantity < 1) {
      for (const lineItemId of moduleItem.lineItemIds || []) {
        await cartStore.removeItem(lineItemId)
      }
    } else {
      const sourceItems = (cart.value?.items || []).filter((item: any) => moduleItem.lineItemIds.includes(item.id))
      for (const lineItem of sourceItems) {
        const baseQuantity = getReadyKitBundleBaseQuantity(lineItem)
        await cartStore.updateItem(lineItem.id, Math.max(1, baseQuantity * newQuantity))
      }
    }
    await loadShippingOptions()
    await loadPaymentProviders()
  } catch (error) {
    console.error('Checkout module update failed:', error)
  } finally {
    updatingItems.value.delete(updateKey)
  }
}

const updateCheckoutComponentQuantity = async (bundleItem: any, moduleItem: any, componentItem: any, newQuantity: number) => {
  if (!componentItem?.lineItemId || updatingItems.value.has(componentItem.lineItemId)) return
  if (newQuantity < 1) {
    await updateCheckoutModuleQuantity(bundleItem, moduleItem, 0)
    return
  }
  updatingItems.value.add(componentItem.lineItemId)
  try {
    await cartStore.updateItem(componentItem.lineItemId, newQuantity)
    await loadShippingOptions()
    await loadPaymentProviders()
  } catch (error) {
    console.error('Checkout component update failed:', error)
  } finally {
    updatingItems.value.delete(componentItem.lineItemId)
  }
}

const resetPayPalState = () => {
  paypalStatus.value = 'idle'
  paypalError.value = ''
  paypalCapture.value = null
}

const normalizeAddress = (source: typeof form.value) => ({
  first_name: source.firstName?.trim() || undefined,
  last_name: source.lastName?.trim() || undefined,
  company: source.company?.trim() || undefined,
  address_1: source.address?.trim() || undefined,
  postal_code: source.postalCode?.trim() || undefined,
  city: source.city?.trim() || undefined,
  country_code: toBackendCountryCode(source.country),
  phone: source.phone?.trim() || undefined
})

const normalizeBillingAddress = (source: typeof billing.value) => ({
  first_name: source.firstName?.trim() || undefined,
  last_name: source.lastName?.trim() || undefined,
  company: source.company?.trim() || undefined,
  address_1: source.address?.trim() || undefined,
  postal_code: source.postalCode?.trim() || undefined,
  city: source.city?.trim() || undefined,
  country_code: toBackendCountryCode(source.country)
})

const normalizeVatInput = (value: string) => value.replace(/\s+/g, '').toUpperCase()
const normalizeVatMatch = (value: unknown) => {
  if (value === true) return 'VALID'
  if (value === false) return 'INVALID'
  const normalized = String(value || '').trim().toUpperCase()
  if (['VALID', 'INVALID', 'NOT_PROCESSED'].includes(normalized)) {
    return normalized
  }
  return normalized || 'NOT_PROCESSED'
}

const normalizeVatName = (value: string) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD') // decompose accents
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ') // replace special chars with space
    .replace(/\s+/g, ' ')
    .trim()

const shouldValidateVat = () => {
  return Boolean(
    isB2BCustomer.value &&
    form.value.vatId &&
    form.value.vatId.trim().length >= 6 &&
    form.value.country
  )
}

const validateVat = async () => {
  if (!shouldValidateVat()) {
    vatStatus.value = 'idle'
    vatDetails.value = null
    return
  }

  vatStatus.value = 'checking'
  const requestId = ++vatRequestId
  const payload = {
    vat_id: normalizeVatInput(form.value.vatId),
    country_code: toBackendCountryCode(form.value.country),
    first_name: form.value.firstName,
    last_name: form.value.lastName,
    company: form.value.company,
    address: form.value.address,
    postal_code: form.value.postalCode,
    city: form.value.city,
  }

  try {
    const headers: Record<string, string> = { 'content-type': 'application/json' }
    if (publishableKey) headers['x-publishable-api-key'] = publishableKey
    const vatUrl = apiBase ? `${apiBase}/store/vat/validate` : '/store/vat/validate'
    const res = await fetch(vatUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    })
    if (requestId !== vatRequestId) return
    if (!res.ok) throw new Error(String(res.status))
    const data = await res.json()
    
    const rawMatches = data?.matches || {}
    const normalizedMatches = {
      name: normalizeVatMatch(rawMatches.name ?? rawMatches.name_match),
      street: normalizeVatMatch(rawMatches.street ?? rawMatches.street_match),
      postal_code: normalizeVatMatch(rawMatches.postal_code ?? rawMatches.postal_match),
      city: normalizeVatMatch(rawMatches.city ?? rawMatches.city_match),
    }

    const proofName = normalizeVatName(data?.name || '')
    const companyName = normalizeVatName(payload.company || '')
    
    // Build Person Names variants
    const fn = normalizeVatName(payload.first_name)
    const ln = normalizeVatName(payload.last_name)
    const personName = `${fn} ${ln}`.trim()
    const personNameReverse = `${ln} ${fn}`.trim()
    
    // Check if proof name contains any valid variant
    const companyMatch = companyName ? proofName.includes(companyName) || companyName.includes(proofName) : null
    
    // Robust person check
    const personMatch = personName ? (
        proofName.includes(personName) || 
        proofName.includes(personNameReverse) ||
        (fn && proofName.includes(fn) && ln && proofName.includes(ln))
    ) : null

    vatDetails.value = {
      ...data,
      matches: normalizedMatches,
      name_checks: {
        company: companyMatch,
        person: personMatch,
      },
    }

    if (!data?.valid) {
      const reason = String(data?.reason || '').toLowerCase()
      if (reason.includes('service') || reason.includes('unavailable') || reason.includes('timeout')) {
        vatStatus.value = 'error'
      } else {
        vatStatus.value = 'invalid'
      }
    } else {
       const isInvalid = (val: string) => val === 'INVALID'
       let hasMismatch = false
       if (isInvalid(normalizedMatches.name)) hasMismatch = true
       if (companyMatch === false && personMatch === false) hasMismatch = true
       if (isInvalid(normalizedMatches.street)) hasMismatch = true
       if (isInvalid(normalizedMatches.postal_code)) hasMismatch = true
       if (isInvalid(normalizedMatches.city)) hasMismatch = true
       
       vatStatus.value = hasMismatch ? 'mismatch' : 'valid'
    }

    if (cart.value?.id) {
      const metadata = {
        ...(cart.value.metadata || {}),
        vat_validation: {
          ...data,
          country_code: payload.country_code,
          vat_id: payload.vat_id,
        },
      }
      await cartStore.updateCart({ metadata })
    }

  } catch (error) {
    if (requestId !== vatRequestId) return
    vatStatus.value = 'error'
    vatDetails.value = null
  }
}

// ------------------------------------------------------------------
// Alma Logic
// ------------------------------------------------------------------
const resetAlmaState = () => {
  almaStatus.value = 'idle'
  almaError.value = ''
  almaPaymentId.value = ''
}

const openAlmaModal = async () => {
  almaModalOpen.value = true
  await nextTick()
}

const closeAlmaModal = () => {
  almaModalOpen.value = false
  if (typeof window !== 'undefined') {
    const wrapper = document.getElementById('alma-in-page-modal-wrapper')
    if (wrapper) wrapper.remove()
    document.body.classList.remove('alma-fixed-body')
  }
  resetAlmaState()
}

const loadAlmaSdk = async () => {
  if (typeof window === 'undefined') return
  if ((window as any).Alma?.InPage) return
  if (almaScriptPromise) return almaScriptPromise

  almaScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/@alma/in-page@2/dist/index.umd.js'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('alma_sdk_failed'))
    document.body.appendChild(script)
  })

  return almaScriptPromise
}

const initializeAlmaInPage = async () => {
  if (!almaMerchantId.value) {
    throw new Error('alma_not_configured')
  }
  await loadAlmaSdk()
  const Alma = (window as any).Alma
  if (!Alma?.InPage?.initialize) {
    throw new Error('alma_unavailable')
  }
  return Alma.InPage.initialize({
    merchantId: almaMerchantId.value,
    amountInCents: almaAmountCents.value,
    installmentsCount: 4,
    selector: '#alma-in-page',
    environment: almaEnvironment.value,
  })
}

const handleAlmaSuccess = async () => {
  if (almaSuccessHandled.value) return
  almaSuccessHandled.value = true
  almaStatus.value = 'approved'
  isSubmitting.value = true
  errorMessage.value = ''
  try {
    if (almaPaymentId.value) {
      await confirmAlmaPayment(almaPaymentId.value)
    }
    await finalizeOrder()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t('checkout.errors.submitFailed')
  } finally {
    isSubmitting.value = false
    closeAlmaModal()
  }
}

const handleAlmaReject = () => {
  if (almaSuccessHandled.value) return
  almaStatus.value = 'error'
  almaError.value = t('checkout.payment.almaRejected')
  errorMessage.value = almaError.value
  closeAlmaModal()
}

const handleAlmaMessage = (event: MessageEvent) => {
  if (!event?.data || typeof event.data !== 'object') return
  if (!isAlmaOrigin(event.origin)) return
  const data = event.data as { from?: string; type?: string; payload?: string }
  if (data.from !== 'checkout' || data.type !== 'in_page_status') return
  if (!almaPaymentId.value) return
  if (data.payload === 'trigger_success_callback') {
    void handleAlmaSuccess()
  }
  if (data.payload === 'trigger_reject_callback') {
    handleAlmaReject()
  }
}

const handlePayPalPopupMessage = (event: MessageEvent) => {
  if (typeof window === 'undefined') return
  if (event.origin !== window.location.origin) return
  if (!event?.data || typeof event.data !== 'object') return
  const data = event.data as any
  if (data.from !== 'readykit') return

  if (data.type === 'paypal_completed') {
    try {
      if (data.order) {
        localStorage.setItem('readykit_last_order', JSON.stringify(data.order))
      }
    } catch (_) {
      // ignore
    }
    const target = String(data.redirect || (locale.value === 'de' ? '/de/order-success' : '/order-success'))
    router.push(target)
    return
  }

  if (data.type === 'paypal_cancelled') {
    // Nothing to do; user is already on checkout in the main window.
    return
  }

  if (data.type === 'paypal_error') {
    errorMessage.value = String(data.message || t('checkout.payment.paypalUnavailable'))
  }
}

const createAlmaPayment = async () => {
  if (!cart.value?.id) {
    throw new Error('missing_cart')
  }
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  if (publishableKey) headers['x-publishable-api-key'] = publishableKey
  const almaBase = apiBase ? `${apiBase}/store/alma` : '/store/alma'
  const res = await fetch(`${almaBase}/create-payment`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ cart_id: cart.value.id }),
  })
  const data = await res.json()
  if (!res.ok) {
    throw new Error(data?.message || t('checkout.payment.almaCreateFailed'))
  }
  return String(data?.id || '')
}

const confirmAlmaPayment = async (paymentId: string) => {
  if (!paymentId) return
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  if (publishableKey) headers['x-publishable-api-key'] = publishableKey
  const almaBase = apiBase ? `${apiBase}/store/alma` : '/store/alma'
  const res = await fetch(`${almaBase}/confirm`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      payment_id: paymentId,
      cart_id: cart.value?.id,
    }),
  })
  const data = await res.json()
  if (!res.ok || data?.ok === false) {
    const message = data?.message || data?.error || t('checkout.payment.almaCreateFailed')
    throw new Error(message)
  }
  return data
}

const startAlmaPayment = async (paymentId: string) => {
  almaStatus.value = 'loading'
  almaError.value = ''
  almaSuccessHandled.value = false
  const inPage = await initializeAlmaInPage()
  inPage.startPayment({
    paymentId,
    onPaymentSucceeded: () => {
      void handleAlmaSuccess()
    },
    onPaymentRejected: () => {
      handleAlmaReject()
    },
    onUserCloseModal: () => {
      almaStatus.value = 'idle'
      closeAlmaModal()
    },
  })
}

// ------------------------------------------------------------------
// Cashpresso Logic
// ------------------------------------------------------------------
let cashpressoScriptPromise: Promise<void> | null = null
let lastCashpressoPayloadKey = ''

const cashpressoScriptId = 'c2CheckoutScript'
const cashpressoContainerId = 'cashpresso-checkout'

const resolveCashpressoScriptUrl = () => {
  if (cashpressoMode.value === 'test') {
    return 'https://my.test-cashpresso.com/ecommerce/v2/checkout/c2_ecom_checkout.all.min.js'
  }
  return 'https://my.cashpresso.com/ecommerce/v2/checkout/c2_ecom_checkout.all.min.js'
}

const resolveCashpressoPlan = (info: any) => {
  if (!info || typeof info !== 'object') return null
  const duration = Number(info.duration || info.term || info.months)
  const lastInstalment = Number(info.lastInstalment || info.last_installment || info.lastInstalmentAmount)
  const amount = Number(info.amount || info.total)
  const toCents = (value: number) =>
    Number.isFinite(value) ? Math.round(value * 100) : undefined
  return {
    duration: Number.isFinite(duration) ? duration : undefined,
    last_instalment: Number.isFinite(lastInstalment) ? toCents(lastInstalment) : undefined,
    amount: Number.isFinite(amount) ? toCents(amount) : undefined,
    existing_plan: info.existingPlan ?? info.existing_plan ?? undefined,
  }
}

const resetCashpressoState = () => {
  cashpressoStatus.value = 'idle'
  cashpressoError.value = ''
  cashpressoToken.value = ''
  cashpressoPaymentInfo.value = null
}

const openCashpressoModal = async () => {
  cashpressoModalOpen.value = true
  await nextTick()
  scheduleCashpressoInit()
}

const closeCashpressoModal = () => {
  cashpressoModalOpen.value = false
  cashpressoSubmitRequested.value = false
  resetCashpressoState()
  clearCashpressoWidget()
}

const clearCashpressoWidget = () => {
  if (typeof window === 'undefined') return
  const existing = document.getElementById(cashpressoScriptId)
  if (existing) existing.remove()
  const container = document.getElementById(cashpressoContainerId)
  if (container) container.innerHTML = ''
  cashpressoScriptPromise = null
  lastCashpressoPayloadKey = ''
  cashpressoWidgetKey.value += 1
}

const cashpressoContainerHasContent = () => {
  if (typeof window === 'undefined') return false
  const container = document.getElementById(cashpressoContainerId)
  return !!container && container.childElementCount > 0
}

const buildCashpressoPayload = () => {
  const countryCode = toBackendCountryCode(form.value.country || selectedCountry.value)
  return {
    partnerApiKey: cashpressoApiKey.value,
    interestFreeDays: String(cashpressoInterestFreeDays.value || 0),
    mode: cashpressoMode.value,
    locale: locale.value === 'de' ? 'de' : 'en',
    email: form.value.email?.trim() || '',
    given: form.value.firstName?.trim() || '',
    family: form.value.lastName?.trim() || '',
    country: countryCode,
    city: form.value.city?.trim() || '',
    zip: form.value.postalCode?.trim() || '',
    addressline: form.value.address?.trim() || '',
    phone: form.value.phone?.trim() || '',
    amount: cashpressoAmountCents.value
      ? (cashpressoAmountCents.value / 100).toFixed(2)
      : '0.00',
  }
}

const buildCashpressoPayloadKey = () => {
  const countryCode = toBackendCountryCode(form.value.country || selectedCountry.value)
  return JSON.stringify({
    email: form.value.email?.trim() || '',
    given: form.value.firstName?.trim() || '',
    family: form.value.lastName?.trim() || '',
    country: countryCode,
    city: form.value.city?.trim() || '',
    zip: form.value.postalCode?.trim() || '',
    addressline: form.value.address?.trim() || '',
    phone: form.value.phone?.trim() || '',
    amount: cashpressoAmountCents.value
      ? Number((cashpressoAmountCents.value / 100).toFixed(2))
      : 0,
  })
}

const updateCashpressoSession = async (token: string, info: any) => {
  if (!cart.value?.id) return
  const plan = resolveCashpressoPlan(info)
  const metadata = {
    ...(cart.value.metadata || {}),
    cashpresso_token: token,
    cashpresso_plan: plan,
    cashpresso_payment_info: info,
  }
  await cartStore.updateCart({ metadata })
  const providerId = resolvePaymentProviderId(selectedPayment.value)
  if (providerId) {
    await cartStore.setPaymentSession(providerId, {
      cashpresso_token: token,
      cashpresso_plan: plan,
      cashpresso_payment_info: info,
    })
  }
}

const buildCashpressoSessionData = () => {
  if (!cashpressoToken.value) return {}
  const plan = resolveCashpressoPlan(cashpressoPaymentInfo.value)
  return {
    cashpresso_token: cashpressoToken.value,
    cashpresso_plan: plan,
    cashpresso_payment_info: cashpressoPaymentInfo.value,
  }
}

const handleCashpressoCallback = async (paymentInfo: any) => {
  cashpressoPaymentInfo.value = paymentInfo || null
  const tokenInput = document.getElementById('cashpressoToken') as HTMLInputElement | null
  const token = tokenInput?.value || paymentInfo?.token || paymentInfo?.c2ecomId || ''
  cashpressoToken.value = token

  if (!token) {
    cashpressoStatus.value = 'error'
    cashpressoError.value = t('checkout.cashpresso.confirmFailed')
    return
  }

  cashpressoStatus.value = 'ready'
  cashpressoError.value = ''
  await updateCashpressoSession(token, paymentInfo)

  if (cashpressoSubmitRequested.value) {
    cashpressoSubmitRequested.value = false
    cashpressoModalOpen.value = false
    await finalizeOrder()
  }
}

const loadCashpressoSdk = async () => {
  if (typeof window === 'undefined') return
  if (!cashpressoReadyForInit.value) {
    resetCashpressoState()
    return
  }

  const payload = buildCashpressoPayload()
  const payloadKey = buildCashpressoPayloadKey()
  if (payloadKey === lastCashpressoPayloadKey) {
    const hasContent = cashpressoContainerHasContent()
    if ((cashpressoStatus.value === 'ready' || cashpressoToken.value) && hasContent) {
      return cashpressoScriptPromise
    }
    if (cashpressoStatus.value === 'loading' && cashpressoScriptPromise && hasContent) {
      return cashpressoScriptPromise
    }
  }
  lastCashpressoPayloadKey = payloadKey

  clearCashpressoWidget()
  resetCashpressoState()
  cashpressoStatus.value = 'loading'

  const container = document.getElementById(cashpressoContainerId)
  if (!container) {
    cashpressoStatus.value = 'idle'
    return
  }

  try {
    getConsentState()
  } catch (e) {
    console.warn('Consent state unavailable:', e)
  }
  registerNecessaryCookie({
    name: 'c2ecomId',
    purpose: 'Cashpresso session identifier',
    provider: 'Cashpresso',
    duration: 'session',
  })
  if (!isConsentGranted('necessary')) {
    cashpressoStatus.value = 'error'
    cashpressoError.value = t('checkout.cashpresso.unavailable')
    return
  }

  const scriptUrl = resolveCashpressoScriptUrl()
  const script = document.createElement('script')
  script.id = cashpressoScriptId
  script.src = scriptUrl
  script.async = true
  script.setAttribute('data-c2-partnerApiKey', payload.partnerApiKey || '')
  script.setAttribute('data-c2-interestFreeDaysMerchant', payload.interestFreeDays)
  script.setAttribute('data-c2-mode', payload.mode)
  script.setAttribute('data-c2-locale', payload.locale)
  script.setAttribute('data-c2-email', payload.email)
  script.setAttribute('data-c2-given', payload.given)
  script.setAttribute('data-c2-family', payload.family)
  script.setAttribute('data-c2-country', payload.country || '')
  script.setAttribute('data-c2-city', payload.city)
  script.setAttribute('data-c2-zip', payload.zip)
  script.setAttribute('data-c2-addressline', payload.addressline)
  script.setAttribute('data-c2-phone', payload.phone)
  script.setAttribute('data-c2-amount', payload.amount)
  script.setAttribute('data-c2-checkoutCallback', 'true')

  cashpressoScriptPromise = new Promise((resolve) => {
    (window as any).c2Checkout = handleCashpressoCallback
    script.onload = () => {
      cashpressoStatus.value = 'ready'
      cashpressoError.value = ''
      resolve()
    }
    script.onerror = () => {
      cashpressoStatus.value = 'error'
      cashpressoError.value = t('checkout.cashpresso.widgetLoadFailed')
      resolve()
    }
    document.body.appendChild(script)
  })

  return cashpressoScriptPromise
}

const scheduleCashpressoInit = () => {
  if (typeof window === 'undefined') return
  if (cashpressoInitTimer) window.clearTimeout(cashpressoInitTimer)
  cashpressoInitTimer = window.setTimeout(() => {
    cashpressoInitTimer = null
    void loadCashpressoSdk()
  }, 200)
}

const scheduleVatValidation = () => {
  if (vatTimer) window.clearTimeout(vatTimer)
  vatTimer = window.setTimeout(() => {
    void validateVat()
  }, 600)
}

const handleVatInput = () => {
  scheduleVatValidation()
}

const handleVatBlur = () => {
  scheduleVatValidation()
}

const scheduleSync = async () => {
  try {
    if (!cart.value?.id) return
    if (!form.value.firstName || !form.value.lastName || !form.value.address || !form.value.postalCode || !form.value.city || !form.value.phone) {
      return
    }
    await syncCart()
    await loadShippingOptions()
  } catch (error) {
    console.warn('Cart sync skipped:', error)
  }
}

const normalizePhotonSuggestion = (feature: any) => {
  const props = feature?.properties || {}
  const street = props.street || props.name || ''
  const housenumber = props.housenumber || ''
  const postcode = props.postcode || ''
  const city = props.city || props.town || props.village || props.county || ''
  const countryCode = String(props.countrycode || '').toUpperCase()
  const labelParts = []
  if (street || housenumber) {
    labelParts.push([street, housenumber].filter(Boolean).join(' '))
  } else if (props.name) {
    labelParts.push(String(props.name))
  }
  if (postcode || city) {
    labelParts.push([postcode, city].filter(Boolean).join(' '))
  }
  if (props.country) {
    labelParts.push(String(props.country))
  }
  return {
    label: labelParts.join(', '),
    street,
    housenumber,
    postcode,
    city,
    countryCode,
  }
}

const fetchAddressSuggestions = async (query: string) => {
  const trimmed = query.trim()
  if (trimmed.length < 4) {
    addressSuggestions.value = []
    addressStatus.value = 'idle'
    return
  }
  if (addressAbort) {
    addressAbort.abort()
  }
  addressAbort = new AbortController()
  addressStatus.value = 'loading'

  const countryCode = toBackendCountryCode(form.value.country || selectedCountry.value || 'DE')
  const lang = locale.value === 'de' ? 'de' : 'en'
  const url = new URL('/address/lookup', window.location.origin)
  url.searchParams.set('q', trimmed)
  url.searchParams.set('limit', '5')
  url.searchParams.set('lang', lang)
  if (countryCode) {
    url.searchParams.set('country', countryCode)
  }

  try {
    const res = await fetch(url.toString(), { signal: addressAbort.signal })
    if (!res.ok) throw new Error(String(res.status))
    const data = await res.json()
    const features = Array.isArray(data?.features) ? data.features : []
    const normalized = features.map(normalizePhotonSuggestion).filter((s) => s.label)
    addressSuggestions.value = normalized
    addressStatus.value = normalized.length ? 'idle' : 'empty'
  } catch (error) {
    if ((error as any)?.name === 'AbortError') return
    addressSuggestions.value = []
    addressStatus.value = 'error'
  }
}

const handleAddressInput = () => {
  if (addressSearchTimer) window.clearTimeout(addressSearchTimer)
  addressSearchTimer = window.setTimeout(() => {
    void fetchAddressSuggestions(form.value.address || '')
  }, 250)
}

const handleAddressBlur = () => {
  window.setTimeout(() => {
    addressFocused.value = false
    addressSuggestions.value = []
  }, 150)
  scheduleSync()
}

const applyAddressSuggestion = (suggestion: any) => {
  const streetLine = [suggestion.street, suggestion.housenumber].filter(Boolean).join(' ')
  if (streetLine) form.value.address = streetLine
  if (suggestion.postcode) form.value.postalCode = suggestion.postcode
  if (suggestion.city) form.value.city = suggestion.city
  if (suggestion.countryCode) {
    const display = toDisplayCountryCode(suggestion.countryCode)
    if (COUNTRIES.some((c) => c.code === display)) {
      form.value.country = display
    }
  }
  addressSuggestions.value = []
  addressFocused.value = false
  addressStatus.value = 'idle'
  scheduleSync()
}

const syncCart = async () => {
  if (!cart.value) return

  const payload: Record<string, any> = {
    email: form.value.email?.trim() || undefined,
    shipping_address: normalizeAddress(form.value),
    billing_address: billingSameAsShipping.value ? normalizeAddress(form.value) : normalizeBillingAddress(billing.value)
  }

  if (form.value.vatId) {
    payload.metadata = { vat_id: form.value.vatId }
  }

  await cartStore.updateCart(payload)
}

const mapShippingOption = (option: any) => {
  const calculated = typeof option.calculated_price === 'number'
    ? option.calculated_price
    : option.calculated_price?.calculated_amount
  const rawDescription = option.data?.description || option.description || ''
  const description = typeof rawDescription === 'string' ? rawDescription.trim() : ''
  const hideDescription =
    /sendcloud|letmeship/i.test(description) ||
    description.toLowerCase().includes('automatisch')

  return {
    id: option.id,
    name: option.name || option.type?.label || t('checkout.shipping.defaultName'),
    description: hideDescription ? '' : description,
    price: calculated ?? option.amount ?? 0
  }
}

const loadShippingOptions = async () => {
  if (!cart.value?.id) return
  shippingOptionsLoading.value = true
  try {
    const options = await cartStore.listShippingOptions()
    shippingOptions.value = options.map(mapShippingOption)
    const allowed = new Set(shippingOptions.value.map((option) => option.id))
    if (selectedShipping.value && !allowed.has(selectedShipping.value)) {
      selectedShipping.value = null
    }
    const existing = cart.value?.shipping_methods?.[0]?.shipping_option_id
    if (existing) {
      selectedShipping.value = existing
    } else if (!selectedShipping.value && shippingOptions.value.length) {
      selectedShipping.value = shippingOptions.value[0].id
    }
  } finally {
    shippingOptionsLoading.value = false
  }
}

const mapPaymentProvider = (provider: any): CheckoutPaymentOption[] => {
  const id = String(provider.id || '').toLowerCase()
  if (id.includes('paypal')) {
    return [
      {
        id: `${provider.id}-card`,
        providerId: provider.id,
        name: t('payments.card'),
        description: t('checkout.payment.cardDescription'),
        fee: 0,
        priority: 1,
        icons: cardBrandIcons,
      },
      {
        id: `${provider.id}-paypal`,
        providerId: provider.id,
        name: t('payments.paypal'),
        description: t('checkout.payment.paypalDescription'),
        fee: 0,
        priority: 2,
        leadIcon: paypalLeadIcon,
      },
    ]
  }
  if (id.includes('alma')) {
    return [
      { id: provider.id, providerId: provider.id, name: t('payments.alma'), description: t('checkout.payment.almaDescription'), fee: 0, priority: 3, leadIcon: almaLeadIcon },
    ]
  }
  if (id.includes('cashpresso')) {
    return [
      { id: provider.id, providerId: provider.id, name: t('payments.cashpresso'), description: t('checkout.payment.cashpressoDescription'), fee: 0, priority: 3, leadIcon: cashpressoLeadIcon },
    ]
  }
  if (id.includes('crypto')) {
    return [
      { id: provider.id, providerId: provider.id, name: t('payments.stablecoin'), description: t('checkout.payment.cryptoDescription'), fee: 0, priority: 4, icons: cryptoIcons, tokenBadges: ['USDC', 'EURC'] },
    ]
  }
  if (id.includes('stripe')) {
    return [
      { id: provider.id, providerId: provider.id, name: t('payments.card'), description: t('checkout.payment.cardDescription'), fee: 0, priority: 1, icons: cardBrandIcons },
    ]
  }
  return [
    { id: provider.id, providerId: provider.id, name: provider.id, description: '', fee: 0, priority: 9 },
  ]
}

const filterPaymentOptionsByCountry = (options: Array<{ id: string; providerId: string; name: string; description: string; fee: number; priority: number }>) => options

const loadPaymentModulesConfig = async () => {
  try {
    const headers: Record<string, string> = {}
    if (publishableKey) headers['x-publishable-api-key'] = publishableKey
    const base = apiBase ? `${apiBase}/store/payment-modules/config` : '/store/payment-modules/config'
    const res = await fetch(base, { headers })
    if (!res.ok) return
    const payload = await res.json()
    paymentModulesConfig.value = {
      paypal: {
        client_id: payload?.paypal?.client_id ?? paymentModulesConfig.value.paypal.client_id,
        environment: payload?.paypal?.environment ?? paymentModulesConfig.value.paypal.environment,
      },
      alma: {
        merchant_id: payload?.alma?.merchant_id ?? paymentModulesConfig.value.alma.merchant_id,
        environment: payload?.alma?.environment ?? paymentModulesConfig.value.alma.environment,
        min_amount_cents: Number(payload?.alma?.min_amount_cents ?? paymentModulesConfig.value.alma.min_amount_cents),
        max_amount_cents: Number(payload?.alma?.max_amount_cents ?? paymentModulesConfig.value.alma.max_amount_cents),
      },
      cashpresso: {
        partner_id: payload?.cashpresso?.partner_id ?? paymentModulesConfig.value.cashpresso.partner_id,
        api_key: payload?.cashpresso?.api_key ?? paymentModulesConfig.value.cashpresso.api_key,
        environment: payload?.cashpresso?.environment ?? paymentModulesConfig.value.cashpresso.environment,
        interest_free_days: Number(payload?.cashpresso?.interest_free_days ?? paymentModulesConfig.value.cashpresso.interest_free_days),
      },
      stablecoin: {
        environment: payload?.stablecoin?.environment ?? paymentModulesConfig.value.stablecoin.environment,
        network_label: payload?.stablecoin?.network_label ?? paymentModulesConfig.value.stablecoin.network_label,
        testnet_active: Boolean(payload?.stablecoin?.testnet_active ?? paymentModulesConfig.value.stablecoin.testnet_active),
        warning: payload?.stablecoin?.warning ?? paymentModulesConfig.value.stablecoin.warning,
      },
    }
  } catch (_) {
    // Fallback bleibt auf den gebauten Runtime-Defaults.
  }
}

const loadPaymentProviders = async () => {
  if (!cart.value?.region_id) return
  paymentOptionsLoading.value = true
  try {
    const [providers, availability] = await Promise.all([
      cartStore.listPaymentProviders(cart.value.region_id),
      loadCryptoAvailability(),
    ])
    const configuredCrypto = availability?.enabled ? availability : null
    const mapped = filterPaymentOptionsByCountry(
      providers
        .flatMap(mapPaymentProvider)
        .filter((entry) => configuredCrypto?.enabled || !String(entry.providerId || '').toLowerCase().includes('crypto'))
        .sort((a, b) => a.priority - b.priority)
    )
    const hasCrypto = mapped.some((entry) => String(entry.providerId || '').toLowerCase().includes('crypto'))
    const nextOptions = [...mapped]
    if (configuredCrypto?.enabled && !hasCrypto && configuredCrypto.provider_id) {
      nextOptions.push(...mapPaymentProvider({ id: configuredCrypto.provider_id }))
    }
    paymentOptions.value = nextOptions.sort((a, b) => a.priority - b.priority)
    const allowed = new Set(nextOptions.map((p) => p.id))
    if (selectedPayment.value && !allowed.has(selectedPayment.value)) {
      selectedPayment.value = null
    }
    if (!selectedPayment.value && paymentOptions.value.length) {
      const stored = localStorage.getItem('readykit_last_payment_provider')
      const match = stored ? paymentOptions.value.find((m) => m.providerId === stored) : null
      selectedPayment.value = match?.id || paymentOptions.value[0].id
    }
    if (selectedPayment.value && selectedPayment.value !== lastInitializedPaymentOption) {
      await nextTick()
      await selectPayment(selectedPayment.value)
      lastInitializedPaymentOption = selectedPayment.value
    }
  } finally {
    paymentOptionsLoading.value = false
  }
}

const resolvePaymentProviderId = (optionId: string | null) => {
  if (!optionId) return null
  const option = paymentOptions.value.find((p) => p.id === optionId)
  return option?.providerId || optionId
}

const ensureShippingMethodAttached = async () => {
  if (!cart.value?.id) return
  const hasShippingMethod = Boolean(cart.value?.shipping_methods?.[0]?.id)
  if (hasShippingMethod) return

  if (!selectedShipping.value) {
    await loadShippingOptions()
  }
  if (!selectedShipping.value) return

  await cartStore.addShippingMethod(selectedShipping.value)
  await cartStore.getCart(cart.value.id)
}

const ensurePaymentSession = async (
  optionId: string,
  mode: 'silent' | 'strict' = 'silent',
  extraData: Record<string, any> = {}
) => {
  const providerId = resolvePaymentProviderId(optionId)
  if (!providerId) return
  const isCashpressoProvider = providerId.toLowerCase().includes('cashpresso')
  const isCryptoProvider = providerId.toLowerCase().includes('crypto')
  const sessionData = isCashpressoProvider
    ? buildCashpressoSessionData()
    : isCryptoProvider
    ? extraData
    : {}

  const tryAttach = async () => {
    await ensureShippingMethodAttached()
    await cartStore.setPaymentSession(providerId, sessionData)
  }

  try {
    await tryAttach()
  } catch (firstError) {
    try {
      if (cart.value?.id) {
        await cartStore.getCart(cart.value.id)
      }
      await loadShippingOptions()
      await tryAttach()
    } catch (secondError) {
      if (mode === 'strict') {
        throw secondError instanceof Error ? secondError : firstError
      }
      console.warn('[Checkout] setPaymentSession deferred', secondError)
    }
  }
}

const selectShipping = async (optionId: string) => {
  if (!cart.value?.id) return
  selectedShipping.value = optionId
  try {
    await cartStore.addShippingMethod(optionId)
  } catch (error) {
    errorMessage.value = t('checkout.errors.shippingOption')
    console.error(error)
  }
}

const selectPayment = async (optionId: string) => {
  if (!cart.value?.id) return
  selectedPayment.value = optionId
  const providerId = resolvePaymentProviderId(optionId)
  const paymentOption = paymentOptions.value.find((p) => p.id === optionId)
  const isPayPalProvider = !!providerId && providerId.toLowerCase().includes('paypal')
  const isCashpressoProvider = !!providerId && providerId.toLowerCase().includes('cashpresso')
  const isCryptoProvider = !!providerId && providerId.toLowerCase().includes('crypto')
  try {
    if (providerId) localStorage.setItem('readykit_last_payment_provider', providerId)
    localStorage.setItem('readykit_last_payment_option', optionId)
  } catch (_) {
    // ignore storage errors
  }
  try {
    // Zahlungsart in Cart-Metadata speichern für OrderSuccess
    if (paymentOption) {
      const metadata = {
        ...(cart.value.metadata || {}),
        payment_option: optionId,
        payment_provider_id: providerId,
        payment_provider_name: paymentOption.name,
        ...(isCryptoProvider
          ? { crypto_selected_token: selectedCryptoToken.value }
          : {
              crypto_payment_request_id: null,
              crypto_selected_token: null,
              crypto_payment_public_token: null,
              crypto_target_amount_display: null,
              crypto_target_currency_code: null,
            }),
      }
      await cartStore.updateCart({ metadata })
    }
    if (isCryptoProvider) {
      await prepareCryptoQuote(false)
    } else {
      await ensurePaymentSession(optionId, 'silent')
    }

    if (isPayPalProvider) {
      resetPayPalState()
      resetCashpressoState()
      closeCashpressoModal()
      clearCashpressoWidget()
    } else if (isCashpressoProvider) {
      resetPayPalState()
      resetCashpressoState()
      closeCashpressoModal()
      clearCashpressoWidget()
    } else if (isCryptoProvider) {
      resetPayPalState()
      resetCashpressoState()
      closeCashpressoModal()
      clearCashpressoWidget()
    } else {
      resetPayPalState()
      resetCashpressoState()
      closeCashpressoModal()
      clearCashpressoWidget()
    }
  } catch (error) {
    // Do not block the user while selecting a payment option.
    // We enforce a strict payment-session setup right before order submission.
    console.warn('[Checkout] payment option setup deferred', error)
  }
}

const prepareCartForSubmission = async () => {
  await syncCart()
  // Persist locale + legal doc versions into the cart metadata so they end up in the order metadata.
  if (cart.value?.id) {
    const metadata: Record<string, any> = {
      ...(cart.value.metadata || {}),
      order_locale: locale.value,
    }
    if (legalTermsVersion) metadata.terms_version = legalTermsVersion
    if (legalPrivacyVersion) metadata.privacy_version = legalPrivacyVersion
    if (legalWithdrawalVersion) metadata.withdrawal_version = legalWithdrawalVersion
    await cartStore.updateCart({ metadata })
  }
  if (selectedShipping.value) {
    await cartStore.addShippingMethod(selectedShipping.value)
  }
  if (selectedPayment.value) {
    let paymentData: Record<string, any> = {}
    if (isCryptoSelected.value) {
      const quote = await prepareCryptoQuote(true)
      if (!quote) {
        throw new Error(t('checkout.payment.cryptoUnavailable'))
      }
      paymentData = buildCryptoSessionData(quote)
    }
    try {
      await ensurePaymentSession(selectedPayment.value, 'strict', paymentData)
    } catch {
      throw new Error(t('checkout.errors.paymentOption'))
    }
  }
}

const finalizeOrder = async () => {
  try {
    const result = await cartStore.completeCart()
    if (result?.order || result?.type === 'order') {
      localStorage.setItem('readykit_last_order', JSON.stringify(result.order))
      if (isCryptoSelected.value && pendingCryptoPublicToken.value) {
        router.push(locale.value === 'de'
          ? `/de/krypto-zahlung/${pendingCryptoPublicToken.value}`
          : `/crypto-payment/${pendingCryptoPublicToken.value}`)
        return
      }
      router.push(locale.value === 'de' ? '/de/order-success' : '/order-success')
      return
    }
    const apiError = result?.error?.message
    errorMessage.value = apiError || t('checkout.errors.submitFailed')
  } catch (error) {
    const message = error instanceof Error ? error.message : ''
    if (
      isCryptoSelected.value &&
      pendingCryptoPublicToken.value &&
      (
        message.includes('Updating Status from "ok" to "temp_failure" is not allowed.') ||
        message.toLowerCase().includes('already completed')
      )
    ) {
      try {
        localStorage.removeItem('readykit_cart_id')
      } catch (_) {
        // ignore storage errors
      }
      cartStore.cart = null
      router.push(locale.value === 'de'
        ? `/de/krypto-zahlung/${pendingCryptoPublicToken.value}`
        : `/crypto-payment/${pendingCryptoPublicToken.value}`)
      return
    }
    throw error
  }
}

const buildPayPalReturnUrl = () => {
  const isDe = router.currentRoute.value.path.startsWith('/de') || locale.value === 'de'
  return new URL(isDe ? '/de/paypal/return' : '/paypal/return', window.location.origin).toString()
}

const buildPayPalCancelUrl = () => {
  const isDe = router.currentRoute.value.path.startsWith('/de') || locale.value === 'de'
  return new URL(isDe ? '/de/paypal/cancel' : '/paypal/cancel', window.location.origin).toString()
}

const clearPayPalQuery = async () => {
  const query = { ...router.currentRoute.value.query }
  delete (query as any).paypal_return
  delete (query as any).paypal_cancel
  delete (query as any).token
  delete (query as any).PayerID
  delete (query as any).payer_id
  await router.replace({ query })
}

const clearAlmaQuery = async () => {
  const query = { ...router.currentRoute.value.query }
  delete (query as any).alma_return
  delete (query as any).alma_cancel
  delete (query as any).pid
  delete (query as any).payment_id
  await router.replace({ query })
}

const handleAlmaReturn = async () => {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)
  const pid = params.get('pid') || params.get('payment_id') || ''
  const hasReturn = params.get('alma_return') === '1' || !!pid
  const hasCancel = params.get('alma_cancel') === '1'
  if (!hasReturn && !hasCancel) return

  if (hasCancel) {
    errorMessage.value = t('checkout.payment.almaRejected')
    await clearAlmaQuery()
    return
  }

  isSubmitting.value = true
  errorMessage.value = ''
  try {
    if (pid) {
      almaPaymentId.value = pid
      await confirmAlmaPayment(pid)
    }
    await cartStore.getCart()
    await finalizeOrder()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t('checkout.errors.submitFailed')
  } finally {
    isSubmitting.value = false
    closeAlmaModal()
    await clearAlmaQuery()
  }
}

const resolvePayPalPopupFeatures = () => {
  if (typeof window === 'undefined') return ''
  const screenWidth = window.screen?.width || 1200
  const screenHeight = window.screen?.height || 900
  const width = Math.min(520, Math.max(360, Math.floor(screenWidth * 0.9)))
  const height = Math.min(820, Math.max(600, Math.floor(screenHeight * 0.9)))
  const left = Math.max(0, Math.floor((screenWidth - width) / 2))
  const top = Math.max(0, Math.floor((screenHeight - height) / 2))
  return `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
}

const startPayPalRedirect = async (landingPage: 'LOGIN' | 'GUEST_CHECKOUT' | 'NO_PREFERENCE' = 'LOGIN') => {
  if (!cart.value?.id) return
  if (paypalRedirectInFlight.value) return

  paypalRedirectInFlight.value = true
  paypalStatus.value = 'loading'
  paypalError.value = ''

  try {
    const headers: Record<string, string> = { 'content-type': 'application/json' }
    if (publishableKey) headers['x-publishable-api-key'] = publishableKey
    const paypalBase = apiBase ? `${apiBase}/store/paypal` : '/store/paypal'
    const res = await fetch(`${paypalBase}/create-order`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        cart_id: cart.value.id,
        return_url: buildPayPalReturnUrl(),
        cancel_url: buildPayPalCancelUrl(),
        landing_page: landingPage,
      }),
    })
    const data = await res.json()
    if (!res.ok) {
      throw new Error(data?.message || t('checkout.payment.paypalCreateFailed'))
    }
    const approvalLink = (data?.links || []).find((link: any) =>
      ['approve', 'payer-action'].includes(String(link?.rel || '').toLowerCase())
    )?.href
    if (!approvalLink) {
      throw new Error(t('checkout.payment.paypalCreateFailed'))
    }
    try {
      localStorage.setItem('readykit_paypal_order_id', String(data?.id || ''))
      localStorage.setItem('readykit_paypal_cart_id', String(cart.value.id))
    } catch (_) {
      // ignore storage errors
    }

    const isSmallViewport = window.innerWidth < 640 || window.innerHeight < 700
    if (isSmallViewport) {
      window.location.href = approvalLink
      return
    }
    const popup = window.open(approvalLink, 'paypal', resolvePayPalPopupFeatures())
    if (!popup) {
      window.location.href = approvalLink
      return
    }
    popup.focus()
  } finally {
    paypalRedirectInFlight.value = false
  }
}

const handlePayPalReturn = async () => {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)
  const hasReturn = params.get('paypal_return') === '1'
  const hasCancel = params.get('paypal_cancel') === '1'
  const token = params.get('token')

  if (!hasReturn && !hasCancel && !token) return

  if (hasCancel) {
    errorMessage.value = t('checkout.payment.paypalCancelled')
    await clearPayPalQuery()
    return
  }

  if (!token) {
    errorMessage.value = t('checkout.payment.paypalCaptureFailed')
    await clearPayPalQuery()
    return
  }

  paypalReturnInProgress.value = true
  isSubmitting.value = true
  errorMessage.value = ''

  try {
    const headers: Record<string, string> = { 'content-type': 'application/json' }
    if (publishableKey) headers['x-publishable-api-key'] = publishableKey
    const paypalBase = apiBase ? `${apiBase}/store/paypal` : '/store/paypal'
    const res = await fetch(`${paypalBase}/capture-order`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ order_id: token }),
    })
    const payload = await res.json()
    if (!res.ok) {
      throw new Error(payload?.message || t('checkout.payment.paypalCaptureFailed'))
    }
	    paypalCapture.value = payload.capture
	    paypalStatus.value = 'approved'
	    if (cart.value?.id) {
	      const paypal_capture = {
	        order_id: token,
	        status: payload.capture?.status,
	        capture_id: payload.capture?.purchase_units?.[0]?.payments?.captures?.[0]?.id,
	      }
	      const metadata = {
	        ...(cart.value.metadata || {}),
	        paypal_capture,
	      }
	      await cartStore.updateCart({ metadata })

	      // Ensure the PayPal order id makes it into the payment session data so Medusa can
	      // verify/capture it during cart completion (prevents unpaid orders).
	      const providerId = resolvePaymentProviderId(selectedPayment.value)
	      if (providerId && providerId.toLowerCase().includes('paypal')) {
	        await cartStore.setPaymentSession(providerId, { paypal_capture })
	      }
	    }
	    await finalizeOrder()
	  } catch (error) {
	    const message = error instanceof Error ? error.message : t('checkout.payment.paypalUnavailable')
	    paypalStatus.value = 'error'
    paypalError.value = message
    errorMessage.value = message
  } finally {
    isSubmitting.value = false
    paypalReturnInProgress.value = false
    await clearPayPalQuery()
  }
}

const submitOrder = async () => {
  if (!isFormValid.value || !cart.value?.id) {
    errorMessage.value = t('checkout.errors.missingFields')
    return
  }
  if (!structuralRule.value.valid) {
    errorMessage.value = t('cart.minModulesNotice')
    return
  }
  
  // Save locale for Order Success page
  try {
    localStorage.setItem('readykit_order_locale', locale.value)
    localStorage.setItem('locale', locale.value)
  } catch (_) {
    // ignore storage errors
  }

  isSubmitting.value = true
  errorMessage.value = ''

  try {
    if (isPayPalSelected.value) {
      if (!paypalClientId.value) {
        errorMessage.value = t('checkout.payment.paypalNotConfigured')
        return
      }
      await prepareCartForSubmission()
      if (!isPayPalAccountSelected.value) {
        await startPayPalRedirect('GUEST_CHECKOUT')
        return
      }
      await startPayPalRedirect('LOGIN')
      return
    }

    if (isCashpressoSelected.value) {
      if (cashpressoStatus.value === 'error') {
        errorMessage.value = t('checkout.errors.cashpressoUnavailable')
        return
      }
      if (!cashpressoReadyToStart.value) {
        errorMessage.value = cashpressoHint.value || t('checkout.errors.cashpressoMissingPlan')
        return
      }
      cashpressoSubmitRequested.value = true
      await prepareCartForSubmission()
      await openCashpressoModal()
      return
    }

    if (isAlmaSelected.value) {
      if (!almaMerchantId.value) {
        errorMessage.value = t('checkout.payment.almaNotConfigured')
        return
      }
      if (!almaAmountCents.value) {
        errorMessage.value = t('checkout.payment.almaUnavailable')
        return
      }
      if (almaAmountCents.value < ALMA_MIN_AMOUNT.value || almaAmountCents.value > ALMA_MAX_AMOUNT.value) {
        errorMessage.value = t('checkout.payment.almaAmountOutOfRange')
        return
      }
      await prepareCartForSubmission()
      const paymentId = await createAlmaPayment()
      if (!paymentId) {
        errorMessage.value = t('checkout.payment.almaCreateFailed')
        return
      }
      almaPaymentId.value = paymentId
      await openAlmaModal()
      await startAlmaPayment(paymentId)
      return
    }

    await prepareCartForSubmission()
    await finalizeOrder()
  } catch (error) {
    console.error('Order submission failed:', error)
    errorMessage.value = error instanceof Error ? error.message : t('checkout.errors.submitFailed')
  } finally {
    isSubmitting.value = false
  }
}

const applyCartToForm = (cartData: any) => {
  if (!cartData) return
  if (cartData.email) form.value.email = cartData.email
  if (cartData?.metadata?.crypto_selected_token) {
    selectedCryptoToken.value = String(cartData.metadata.crypto_selected_token).toUpperCase() as 'USDC' | 'EURC'
  }
  if (cartData.shipping_address) {
    const addr = cartData.shipping_address
    form.value.firstName = addr.first_name || form.value.firstName
    form.value.lastName = addr.last_name || form.value.lastName
    form.value.company = addr.company || form.value.company
    form.value.address = addr.address_1 || form.value.address
    form.value.postalCode = addr.postal_code || form.value.postalCode
    form.value.city = addr.city || form.value.city
    form.value.country = toDisplayCountryCode(addr.country_code) || form.value.country || 'AT'
    form.value.phone = addr.phone || form.value.phone
  }
  if (cartData.billing_address && !billingSameAsShipping.value) {
    const addr = cartData.billing_address
    billing.value.firstName = addr.first_name || billing.value.firstName
    billing.value.lastName = addr.last_name || billing.value.lastName
    billing.value.company = addr.company || billing.value.company
    billing.value.address = addr.address_1 || billing.value.address
    billing.value.postalCode = addr.postal_code || billing.value.postalCode
    billing.value.city = addr.city || billing.value.city
    billing.value.country = toDisplayCountryCode(addr.country_code) || billing.value.country || 'AT'
  }
}

const refreshSelectedCountry = () => {
  try {
    const stored = localStorage.getItem('readykit_region')
    if (stored) {
      selectedCountry.value = stored.toUpperCase()
      return
    }
  } catch (_) {
    // ignore
  }
  const cartCountry =
    toDisplayCountryCode(cart.value?.shipping_address?.country_code) ||
    toDisplayCountryCode(cart.value?.billing_address?.country_code) ||
    ''
  if (cartCountry) {
    selectedCountry.value = String(cartCountry).toUpperCase()
    return
  }
  const fallback = form.value.country || toDisplayCountryCode(cart.value?.shipping_address?.country_code) || 'DE'
  selectedCountry.value = String(fallback).toUpperCase()
}

const syncRegionWithSelection = async (country: string) => {
  if (!country) return
  if (lastRegionSync.value === country) return
  try {
    await cartStore.setRegionByCountry(country)
    await loadShippingOptions()
    await ensureShippingMethodAttached()
    await loadPaymentProviders()
    lastRegionSync.value = country
  } catch (error) {
    lastRegionSync.value = ''
    throw error
  }
}

const handleRegionChange = (event?: Event) => {
  const detailCountry = String((event as CustomEvent<string> | undefined)?.detail || '').toUpperCase()
  if (detailCountry) {
    selectedCountry.value = detailCountry
    form.value.country = detailCountry
    syncRegionWithSelection(detailCountry)
    return
  }
  refreshSelectedCountry()
  if (selectedCountry.value) {
    form.value.country = selectedCountry.value
    syncRegionWithSelection(selectedCountry.value)
  }
}

onMounted(async () => {
  await loadPaymentModulesConfig()
  let current = await cartStore.getCart()
  if (!current) {
    current = await cartStore.createCart()
  }
  if (!medusaStore.hydrated && !medusaStore.loading) {
    await medusaStore.hydrateCatalog()
  }
  applyCartToForm(current)
  refreshSelectedCountry()
  if (selectedCountry.value) {
    form.value.country = selectedCountry.value
  }
  await syncRegionWithSelection(selectedCountry.value)
  scheduleCashpressoQuote()
  scheduleAlmaQuote()
  if (shouldValidateVat()) {
    scheduleVatValidation()
  }
  await handleAlmaReturn()
  await handlePayPalReturn()
  window.addEventListener('message', handleAlmaMessage)
  window.addEventListener('message', handlePayPalPopupMessage)
  window.addEventListener('readykit-region-change', handleRegionChange)
})

onUnmounted(() => {
  window.removeEventListener('readykit-region-change', handleRegionChange)
  if (addressSearchTimer) window.clearTimeout(addressSearchTimer)
  if (addressAbort) addressAbort.abort()
  if (vatTimer) window.clearTimeout(vatTimer)
  if (cashpressoInitTimer) window.clearTimeout(cashpressoInitTimer)
  if (cashpressoQuoteTimer) window.clearTimeout(cashpressoQuoteTimer)
  if (almaQuoteTimer) window.clearTimeout(almaQuoteTimer)
  clearCashpressoWidget()
  window.removeEventListener('message', handleAlmaMessage)
  window.removeEventListener('message', handlePayPalPopupMessage)
})

watch(() => form.value.country, (country) => {
  if (!country) return
  selectedCountry.value = String(country).toUpperCase()
  try {
    localStorage.setItem('readykit_region', selectedCountry.value)
  } catch (_) {
    // ignore storage errors
  }
  syncRegionWithSelection(selectedCountry.value)
})

watch(
  [() => form.value.country, () => cashpressoAmountCents.value, () => almaAmountCents.value, () => cart.value?.region_id],
  () => {
    scheduleCashpressoQuote()
    scheduleAlmaQuote()
  }
)

watch(selectedPayment, () => {
    if (!isPayPalSelected.value) {
      resetPayPalState()
    }
  if (!isCashpressoSelected.value) {
    resetCashpressoState()
    clearCashpressoWidget()
    closeCashpressoModal()
  }
  if (!isAlmaSelected.value) {
    closeAlmaModal()
  }
  if (!isCryptoSelected.value) {
    cryptoQuote.value = null
    pendingCryptoPublicToken.value = ''
  }
})

watch(
  [
    isCashpressoSelected,
    cashpressoModalOpen,
    () => form.value.firstName,
    () => form.value.lastName,
    () => form.value.address,
    () => form.value.postalCode,
    () => form.value.city,
    () => form.value.country,
    () => form.value.phone,
    () => form.value.email,
    () => cashpressoAmountCents.value,
    () => locale.value,
  ],
  async () => {
    if (!isCashpressoSelected.value || !cashpressoModalOpen.value) return
    await nextTick()
    scheduleCashpressoInit()
  }
)

watch(
  () => locale.value,
  async () => {
    lastInitializedPaymentOption = null
    if (isCashpressoSelected.value) {
      resetCashpressoState()
      clearCashpressoWidget()
    }
    await nextTick()
    await loadShippingOptions()
    await ensureShippingMethodAttached()
    await loadPaymentProviders()
  }
)

watch(
  [selectedCryptoToken, isCryptoSelected, () => cart.value?.id],
  async ([token, cryptoSelected]) => {
    if (!cryptoSelected || !token || !cart.value?.id) return
    await prepareCryptoQuote(false)
  }
)

watch(
  [
    isB2BCustomer,
    () => form.value.vatId,
    () => form.value.firstName,
    () => form.value.lastName,
    () => form.value.company,
    () => form.value.address,
    () => form.value.postalCode,
    () => form.value.city,
    () => form.value.country,
  ],
  () => {
    if (!isB2BCustomer.value) {
      vatStatus.value = 'idle'
      vatDetails.value = null
      return
    }
    if (shouldValidateVat()) {
      scheduleVatValidation()
    }
  }
)
</script>

<style scoped>
@reference "tailwindcss";
.spinner {
  @apply w-4 h-4 border-2 border-white border-t-transparent rounded-full;
}
</style>
